import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from "react-icons/tb";
import Modal from "../modal/Modal";
import { parseProvision } from "./parseProvision";
import CoverageAnswer from "./CoverageAnswer";
import { fetchNetworkDefCompDetails, fetchServiceCategoryCodes } from "../../utils/apiUtils";
import {
  askDefaultsForBrowse,
  categoryForSlotCodes,
  familyLabel,
  groupComponentsBySlot,
  inferSlotName,
  slotsMentioningCategory,
} from "../../utils/slotBrowse";

const CODES_PAGE_SIZE = 40;

// Ported from benefit-checker PlanView cluster — styled via Coverage.scss

function Skeleton({ rows = 1, height = 14, width = "100%", className = "" }) {
    return (
        <div className={`coverage-skel ${className}`.trim()}>
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} className="coverage-skel__bar" style={{ height, width }} />
            ))}
        </div>
    );
}
function ListRowsSkeleton({ rows = 4 }) { return <Skeleton rows={rows} height={36} />; }
function ModalSkeleton() { return <Skeleton rows={8} height={18} />; }

const sourceKey = (s) => `${s?.kind || "component"}\0${s?.name || ""}`;

export function mergeProvisionSources(...lists) {
  const byName = new Map();
  const rank = { rider: 3, component: 2, plan: 1 };
  for (const list of lists) {
    const items = Array.isArray(list) ? list : list ? [list] : [];
    for (const item of items) {
      const name = typeof item === "string" ? item : item?.name;
      if (!name) continue;
      const kind = typeof item === "string"
        ? (name === "Plan" ? "plan" : "component")
        : (item.kind || "component");
      const key = String(name).toLowerCase();
      const prev = byName.get(key);
      if (!prev || (rank[kind] || 0) > (rank[prev.kind] || 0)) {
        byName.set(key, { kind, name });
      }
    }
  }
  return [...byName.values()];
}

function provisionSourceList(p) {
  return mergeProvisionSources(p?.sources, p?.source);
}

function ProvisionOrigin({ sources }) {
  const items = (sources || []).filter((s) => s.name && s.kind !== "plan");
  const fallback = !items.length && (sources || []).some((s) => s.kind === "plan")
    ? [{ kind: "plan", name: "Plan" }]
    : items;
  if (!fallback.length) return null;
  const visible = fallback.slice(0, 4);
  const extra = fallback.length - visible.length;
  return (
    <div className="prov-from" onClick={(e) => e.stopPropagation()}>
      <span className="prov-from-label">From</span>
      <ul>
        {visible.map((s) => (
          <li key={sourceKey(s)}>
            <span className={`badge origin-${s.kind}`}>
              {s.kind === "rider" ? "Rider" : s.kind === "plan" ? "Plan" : "Component"}
            </span>
            <span className="prov-from-name">{s.name}</span>
          </li>
        ))}
      </ul>
      {extra > 0 && (
        <details className="prov-from-more">
          <summary>+{extra} more</summary>
          <ul>
            {fallback.slice(4).map((s) => (
              <li key={sourceKey(s)}>
                <span className={`badge origin-${s.kind}`}>
                  {s.kind === "rider" ? "Rider" : s.kind === "plan" ? "Plan" : "Component"}
                </span>
                <span className="prov-from-name">{s.name}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* ---- lines 10-16 ---- */
function StatusBadge({ status, label }) {
  const text = label || status || '—'
  const cls = String(status || '').toLowerCase() === 'a' ? 'active'
    : ['t', 'i', 'd'].includes(String(status || '').toLowerCase()) ? 'terminated'
      : ''
  return <span className={`badge ${cls}`}>{text}</span>
}

/* ---- lines 639-679 ---- */
function VersionChips({ versions, onSelect, collapseAfter = 0 }) {
  const [expanded, setExpanded] = useState(false)
  if (!versions?.length) return null
  const limit = collapseAfter > 0 && !expanded ? collapseAfter : versions.length
  const visible = versions.slice(0, limit)
  const hidden = versions.length - visible.length

  return (
    <div className="version-chips" onClick={(e) => e.stopPropagation()}>
      {visible.map((v) => (
        <button
          key={v.histFactKey || v.label}
          type="button"
          className={`version-chip ${v.isAsOf ? 'on' : ''} ${v.isOpen ? 'open-ver' : ''}`}
          title={[v.name, v.statusLabel, v.label].filter(Boolean).join(' · ')}
          onClick={() => onSelect?.(v)}
        >
          {v.label}
        </button>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="version-more"
          onClick={() => setExpanded(true)}
        >
          +{hidden} more
        </button>
      )}
      {expanded && collapseAfter > 0 && versions.length > collapseAfter && (
        <button
          type="button"
          className="version-more"
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </div>
  )
}

/* ---- lines 729-833 ---- */
function NetworkWiringGuide({ highlight = null, compact = false }) {
  const [open, setOpen] = useState(!compact)
  const steps = [
    {
      id: 'provider',
      title: 'Provider',
      short: 'Who rendered care',
      blurb: 'Supplier, location, or practitioner on the claim.',
    },
    {
      id: 'supplierNetwork',
      title: 'Supplier network',
      short: 'Are they contracted?',
      blurb: 'Provider roster (HCC like PNBSW). Membership here means they are affiliated with a contracted group — not yet the member’s cost share.',
    },
    {
      id: 'contract',
      title: 'Contract',
      short: 'The bridge',
      blurb: 'Links the supplier to fee schedules and which benefit networks they participate in. Without this, roster ≠ plan INN.',
    },
    {
      id: 'benefitNetwork',
      title: 'Benefit network',
      short: 'How the plan pays',
      blurb: 'Plan INN bucket for deductibles, copays, and OOP. This is what drives member cost share when care is in-network.',
    },
    {
      id: 'plan',
      title: 'Plan',
      short: 'What members see as INN',
      blurb: 'Network Definition rider lists which supplier networks count as in-network for this plan’s members.',
    },
  ]

  const youAreHere = {
    provider: 'You’re on a provider. Check contracts and supplier-network affiliations to see how they can become INN on a plan.',
    supplierNetwork: 'You’re on a supplier network (roster). Plans pull this into INN via Network Definition; contracts decide which benefit networks apply.',
    contract: 'You’re looking at the bridge: contracts connect providers to benefit networks and pricing.',
    benefitNetwork: 'You’re on a benefit network (plan INN bucket). Suppliers reach it through contracts; plans use it for cost share.',
    plan: 'You’re on the plan. Network Definition riders decide which supplier networks count as in-network for members.',
  }

  return (
    <aside className={`network-guide ${compact ? 'compact' : ''}`} aria-label="How networks connect">
      <div className="network-guide-head">
        <div>
          <h3>How networks connect</h3>
          {!open && (
            <p className="muted">Supplier networks (who) vs benefit networks (how the member pays).</p>
          )}
        </div>
        {compact && (
          <button
            type="button"
            className="coverage__link-btn"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide guide' : 'Show guide'}
          </button>
        )}
      </div>

      {open && (
        <>
          <p className="network-guide-lead">
            HRP uses “network” for two different things: who is contracted, and how the member pays.
          </p>

          <div className="network-contrast">
            <div className={`network-contrast-card who ${highlight === 'supplierNetwork' || highlight === 'provider' ? 'on' : ''}`}>
              <span className="network-contrast-tag">Who</span>
              <strong>Supplier network</strong>
              <p>Provider roster / affiliation.</p>
            </div>
            <div className="network-contrast-bridge" aria-hidden>
              <span>via contract</span>
            </div>
            <div className={`network-contrast-card how ${highlight === 'benefitNetwork' || highlight === 'plan' ? 'on' : ''}`}>
              <span className="network-contrast-tag">How</span>
              <strong>Benefit network</strong>
              <p>Plan INN cost-share bucket.</p>
            </div>
          </div>

          <ol className="network-flow">
            {steps.map((step, i) => (
              <li
                key={step.id}
                className={`network-flow-step ${highlight === step.id ? 'on' : ''}`}
              >
                <span className="network-flow-num">{i + 1}</span>
                <div className="network-flow-copy">
                  <div className="network-flow-title">{step.title}</div>
                  <div className="network-flow-short">{step.short}</div>
                </div>
              </li>
            ))}
          </ol>

          {highlight && youAreHere[highlight] && (
            <p className="network-guide-here">
              <strong>You are here.</strong> {youAreHere[highlight]}
            </p>
          )}

          {!compact && (
            <p className="network-guide-example">
              <strong>Example.</strong> Supplier <code>DELTA MEDICAL PA</code> sits on
              supplier network <code>PNBSW</code>. A contract maps that into benefit
              network <code>101</code>. The plan’s Network Definition rider then treats
              <code> PNBSW </code> as in-network for members — so claims pay at INN cost share.
            </p>
          )}
        </>
      )}
    </aside>
  )
}

/* ---- lines 1238-1341 ---- */
function CategoryCodesSidebar({
  name,
  asOf,
  cacheEntry,
  onCacheUpdate,
  onHide,
  fromProvision = false,
  onCheckCode,
}) {
  const listRef = useRef(null)
  const sentinelRef = useRef(null)
  const loadingRef = useRef(false)
  const requestIdRef = useRef(0)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const loadPage = useCallback(async (offset, { append = false } = {}) => {
    if (!name || !asOf || loadingRef.current) return
    loadingRef.current = true
    const requestId = ++requestIdRef.current
    if (append) setLoadingMore(true)
    else setLoading(true)
    setErr('')
    try {
      const page = await fetchServiceCategoryCodes({
        categoryName: name,
        asOfDate: asOf,
        limit: CODES_PAGE_SIZE,
        offset,
        includeSummary: offset === 0 ? '1' : '0',
      })
      if (requestId !== requestIdRef.current) return
      const codes = page.codes || []
      onCacheUpdate((prev) => {
        const prior = append ? (prev?.codes || []) : []
        return {
          categoryName: page.categoryName || name,
          categoryType: page.categoryType ?? prev?.categoryType ?? null,
          total: page.total ?? prev?.total ?? null,
          summary: offset === 0 ? (page.summary || []) : (prev?.summary || []),
          codes: [...prior, ...codes],
          hasMore: !!page.hasMore,
          nextOffset: offset + codes.length,
        }
      })
    } catch (e) {
      if (requestId !== requestIdRef.current) return
      setErr(e.message || 'Failed to load codes')
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [name, asOf, onCacheUpdate])

  useEffect(() => {
    setErr('')
    loadingRef.current = false
    requestIdRef.current += 1
    if (!name || !asOf) return undefined
    if (cacheEntry) return undefined
    loadPage(0, { append: false })
    return undefined
  }, [name, asOf, cacheEntry, loadPage])

  useEffect(() => {
    if (!cacheEntry?.hasMore || !name) return undefined
    const root = listRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel) return undefined

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingRef.current) return
        loadPage(cacheEntry.nextOffset ?? cacheEntry.codes?.length ?? 0, { append: true })
      },
      { root, rootMargin: '120px', threshold: 0 }
    )
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [cacheEntry?.hasMore, cacheEntry?.nextOffset, cacheEntry?.codes?.length, name, loadPage])

  if (!name) {
    return (
      <aside className="service-codes-sidebar">
        <div className="service-codes-sidebar-head">
          <h3>Codes</h3>
          {onHide && (
            <button type="button" className="coverage__link-btn" onClick={onHide}>
              Hide
            </button>
          )}
        </div>
        <p className="muted service-codes-empty">Select a service to view CPT / HCPCS codes.</p>
      </aside>
    )
  }

  const codes = cacheEntry?.codes || []
  const shown = codes.length
  const totalLabel = cacheEntry?.total != null
    ? `${shown} of ${cacheEntry.total}`
    : `${shown}${cacheEntry?.hasMore ? '+' : ''}`

  return (
    <aside className="service-codes-sidebar">
      <div className="service-codes-sidebar-head">
        <div>
          <h3>Codes</h3>
          <p className="service-codes-category-name" title={name}>{name}</p>
          {cacheEntry && (
            <p className="muted">
              {totalLabel} shown
              {fromProvision ? ' · from provision' : ''}
            </p>
          )}
        </div>
        <div className="service-codes-sidebar-actions">
          {cacheEntry?.categoryType && (
            <span className="chip static">{cacheEntry.categoryType}</span>
          )}
          {onHide && (
            <button type="button" className="coverage__link-btn" onClick={onHide}>
              Hide
            </button>
          )}
        </div>
      </div>

      {!!cacheEntry?.summary?.length && (
        <div className="chip-wrap service-codes-summary">
          {cacheEntry.summary.map((s) => (
            <span
              key={s.codeType}
              className="chip static"
              title={s.approximate ? 'Range count (approx.)' : undefined}
            >
              {s.codeTypeLabel} {s.count}{s.approximate ? '*' : ''}
            </span>
          ))}
        </div>
      )}

      {err && <div className="error">{err}</div>}

      <div className="service-codes-list" ref={listRef}>
        {loading && !cacheEntry && (
          <div className="stack pad-sm" aria-busy="true" aria-label="Loading codes">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="mb-sm">
                <Skeleton width="4rem" height="0.7rem" className="mb-sm" />
                <Skeleton width={`${55 + i * 6}%`} height="0.85rem" />
              </div>
            ))}
          </div>
        )}

        {codes.map((c) => (
          <div key={`${c.codeType || ''}-${c.code}`} className="code-row">
            <div className="code-row-main">
              <span className="badge">{c.codeTypeLabel || 'Code'}</span>
              <code className="code-row-code">{c.code}</code>
              {onCheckCode && (
                <button
                  type="button"
                  className="coverage__link-btn code-row-check"
                  onClick={() => onCheckCode(c)}
                  title={`Ask HRP what this plan covers for ${c.code}`}
                >
                  Check
                </button>
              )}
            </div>
            {c.description && (
              <span className="code-row-desc">{c.description}</span>
            )}
          </div>
        ))}

        {!loading && cacheEntry && !codes.length && !err && (
          <p className="muted service-codes-empty">No codes mapped for this service.</p>
        )}

        {cacheEntry?.hasMore && (
          <div ref={sentinelRef} className="service-codes-sentinel" aria-hidden />
        )}
        {loadingMore && (
          <p className="muted service-codes-loading-more">Loading more…</p>
        )}
        {cacheEntry && !cacheEntry.hasMore && codes.length > 0 && (
          <p className="muted service-codes-end">End of codes</p>
        )}
      </div>
    </aside>
  )
}

/* ---- lines 1343-1665 ---- */
function ProvisionList({
  provisions,
  filter,
  variableValues = {},
  focusService = null,
  onOpenCodes = null,
}) {
  const filtered = useMemo(() => {
    const q = (filter || '').trim().toLowerCase()
    if (!q) return provisions || []
    return (provisions || []).filter((p) =>
      (p.text || '').toLowerCase().includes(q)
      || (p.type || '').toLowerCase().includes(q)
      || (p.tierName || '').toLowerCase().includes(q)
      || provisionSourceList(p).some((s) => s.name.toLowerCase().includes(q))
    )
  }, [provisions, filter])

  if (!filtered.length) return <p className="muted">No matching provisions.</p>
  return (
    <div className="stack">
      {filtered.map((p, i) => {
        const parsed = parseProvision(p, variableValues)
        const categories = parsed.categories || []
        const primary = pickCodeCategory(categories, focusService)
        const canOpen = !!onOpenCodes && !!primary
        return (
          <div
            key={i}
            className={`prov ${p.isInNetwork ? 'inn' : 'oon'}${canOpen ? ' prov-clickable' : ''}`}
            onClick={canOpen ? () => onOpenCodes(primary) : undefined}
            onKeyDown={canOpen ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpenCodes(primary)
              }
            } : undefined}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
          >
            <div className="prov-meta">
              {p.type && <span className="badge">{p.type}</span>}
              {p.tierName && (
                <span className={`badge ${p.isInNetwork ? 'inn' : 'oon'}`}>
                  {p.isInNetwork ? 'INN' : 'OON'} · {p.tierName}
                </span>
              )}
              {canOpen && (
                <button
                  type="button"
                  className="coverage__link-btn prov-codes-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenCodes(primary)
                  }}
                >
                  View codes
                </button>
              )}
            </div>
            <div className="prov-text">{p.text}</div>
            <ProvisionOrigin sources={provisionSourceList(p)} />
            {categories.length > 1 && onOpenCodes && (
              <div className="prov-code-cats" onClick={(e) => e.stopPropagation()}>
                <span className="prov-code-cats-label">Categories</span>
                <ChipList items={categories} onChipClick={onOpenCodes} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function summaryBucket(provision, parsed) {
  const type = provision?.type || ''
  if (parsed.isExclusionOverride) return 'benefit'
  if (parsed.isExclusion || (/^Exclusion/i.test(type) && !/Override/i.test(type))) return 'exclusion'
  if (parsed.limit || /^Limit/i.test(type)) return 'limit'
  if (parsed.authorization || parsed.referralRequired || /UtilizationManagement|ExemptionItem/i.test(type)) {
    return 'authorization'
  }
  if (parsed.accum || /Deductible|OutOfPocket/i.test(type)) return 'accumulator'
  if (parsed.benefitCalcTier || parsed.carveOut || /Repricer|COB|Processing/i.test(type)) return 'processing'
  if (parsed.benefitPercent || parsed.thenBenefit || parsed.isDefault || /BenefitProvision|DefaultBenefit/i.test(type)) {
    return 'benefit'
  }
  return 'other'
}

function pickCodeCategory(categories, focusService = null) {
  const cats = (categories || []).filter(Boolean)
  if (!cats.length) return null
  const focus = (focusService || '').trim().toLowerCase()
  if (focus) {
    const exact = cats.find((c) => c.toLowerCase() === focus)
    if (exact) return exact
    const partial = cats.find((c) => c.toLowerCase().includes(focus) || focus.includes(c.toLowerCase()))
    if (partial) return partial
  }
  return cats[0]
}

function ChipList({ items, className = '', onChipClick = null }) {
  if (!items?.length) return null
  return (
    <div className={`chip-wrap prov-chips ${className}`}>
      {items.map((item) => (
        onChipClick ? (
          <button
            key={item}
            type="button"
            className="chip chip-btn"
            onClick={(e) => {
              e.stopPropagation()
              onChipClick(item)
            }}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="chip static">{item}</span>
        )
      ))}
    </div>
  )
}

function SummaryRows({ rows, className = '' }) {
  const list = (rows || []).filter((r) => r && r[1] != null && r[1] !== '')
  if (!list.length) return null
  return (
    <dl className={`prov-summary ${className}`.trim()}>
      {list.map(([label, value], i) => (
        <div key={`${label}-${i}`} className="prov-summary-row">
          <dt>{label}</dt>
          <dd>{Array.isArray(value) ? <ChipList items={value} /> : value}</dd>
        </div>
      ))}
    </dl>
  )
}

function bucketLabel(bucket, parsed, type) {
  if (bucket === 'exclusion') return 'Not covered'
  if (bucket === 'limit') return 'Limit'
  if (bucket === 'authorization') {
    if (parsed.authorization?.waived) return 'No prior auth'
    if (parsed.referralRequired && !parsed.authorization?.required) return 'Referral needed'
    return 'Prior auth'
  }
  if (bucket === 'accumulator') return parsed.accum?.kind || 'Plan limit'
  if (bucket === 'processing') return parsed.carveOut ? 'Vendor carve-out' : 'Processing'
  if (bucket === 'benefit') {
    if (parsed.isExclusionOverride) return 'Covered anyway'
    if (parsed.isDefault) return 'Catch-all'
    return 'Covered'
  }
  return type || 'Other'
}

function titleCaseWord(value) {
  return String(value || '').replace(/\b\w/g, (c) => c.toUpperCase())
}

function ProvisionSummaryCard({
  provision: p,
  parsed,
  focusService = null,
  onOpenCodes = null,
}) {
  const bucket = summaryBucket(p, parsed)
  const focus = (focusService || '').trim().toLowerCase()
  const categories = parsed.categories || []
  const who = provisionWhoLabel(parsed)
  const when = provisionWhenLabel(parsed)
  const focusedCats = focus
    ? categories.filter((c) => c.toLowerCase() === focus || c.toLowerCase().includes(focus))
    : categories
  const otherCats = (focus
    ? categories.filter((c) => !(c.toLowerCase() === focus || c.toLowerCase().includes(focus)))
    : categories
  ).filter((c) => !COMPANION_CATEGORY.test(c) && !(when && /emergent/i.test(c)))
  // When viewing a service, don't restate the obvious "Applies to Acupuncture"
  const showApplies = !focus || otherCats.length > 0 || !focusedCats.length
  const appliesCats = showApplies
    ? (focus && otherCats.length ? otherCats : categories)
    : []
  const applies = parsed.authorization?.appliesText && !focus
    ? parsed.authorization.appliesText
    : (appliesCats.length ? appliesCats : null)
  const primaryCodeCategory = pickCodeCategory(categories, focusService)
  const canOpenCodes = !!onOpenCodes && !!primaryCodeCategory

  const openPrimaryCodes = (e) => {
    e?.stopPropagation?.()
    if (primaryCodeCategory) onOpenCodes?.(primaryCodeCategory)
  }

  let headline = null
  let subline = null
  let rows = []
  let callouts = []

  if (bucket === 'exclusion') {
    headline = appliesCats.length > 1
      ? `Not covered · ${appliesCats.length} services`
      : parsed.unlessConditions?.length
        ? 'Not covered, unless…'
        : 'Not covered'
    subline = parsed.unlessConditions?.length
      ? 'The plan only pays if the conditions below are met. Otherwise the member pays the full allowed amount.'
      : 'The plan does not pay. The member is responsible for the full allowed amount.'
    if (appliesCats.length > 1) callouts = appliesCats
    else if (appliesCats.length === 1 && !focus) callouts = appliesCats
    rows = [
      parsed.excludes.length && ['Still covered if', parsed.excludes],
      parsed.unlessConditions?.length && ['Covered only if', parsed.unlessConditions],
      (parsed.message || parsed.authorization?.message) && [
        'Message on the EOB',
        parsed.message || parsed.authorization?.message,
      ],
    ]
  } else if (bucket === 'limit') {
    headline = parsed.limit?.text || parsed.label || 'Visit or dollar limit'
    const period = parsed.limit?.period || parsed.period
    subline = period
      ? `This is the most the plan will allow ${String(period).toLowerCase().startsWith('per ') ? period.toLowerCase() : `per ${period.toLowerCase()}`}.`
      : 'This is the most the plan will allow for these services.'
    rows = [
      applies && ['Applies to', applies],
      parsed.excludes.length && ['Does not count when', parsed.excludes],
      parsed.networks.length
        && !parsed.excludes.some((e) => String(e).startsWith('network '))
        && ['Networks', parsed.networks],
      parsed.memberRelationships?.length && ['Who it applies to', parsed.memberRelationships],
      parsed.memberAgeText && ['Member age', parsed.memberAgeText],
      parsed.label && parsed.limit?.text && ['Shown on the plan as', parsed.label],
    ]
  } else if (bucket === 'authorization') {
    const auth = parsed.authorization
    headline = auth?.waived
      ? 'No prior authorization needed'
      : auth?.required
        ? 'Prior authorization required'
        : parsed.referralRequired
          ? 'Referral required'
          : 'Review may be required'
    subline = auth?.waived
      ? 'Claims can pay without an authorization on file.'
      : auth?.threshold
        ? `Required after ${auth.threshold.toLowerCase()}${auth?.type ? ` · ${auth.type}` : ''}.`
        : parsed.referralRequired
          ? 'A referral is required before this service is covered.'
          : 'The claim may pend or pay at a penalty if review is missing.'
    rows = [
      applies && ['Applies to', applies],
      (auth?.period || parsed.period) && ['Over this period', auth?.period || parsed.period],
      auth?.penalty && ['If there is no authorization', auth.penalty.text],
      parsed.sameClaimAs?.length && ['Only with these services on the same claim', parsed.sameClaimAs],
      parsed.sameConfinementAs?.length && ['Only during the same confinement as', parsed.sameConfinementAs],
      parsed.sameDayAs?.length && ['Only on the same day as', parsed.sameDayAs],
      parsed.claimSources?.length && ['When the claim is submitted as', parsed.claimSources],
      (auth?.message || parsed.message) && ['Message on the EOB', auth?.message || parsed.message],
      (auth?.messageCode || parsed.messageCode) && [
        'EOB message code',
        auth?.messageCode || parsed.messageCode,
      ],
    ]
  } else if (bucket === 'accumulator') {
    const kind = parsed.accum?.kind || 'Plan limit'
    const level = parsed.accum?.level ? titleCaseWord(parsed.accum.level) : ''
    headline = parsed.accum?.display
      ? parsed.accum.display
      : (parsed.accum?.text || kind)
    subline = [level, kind].filter(Boolean).join(' ') || 'Counts toward the deductible or out-of-pocket maximum'
    if (parsed.period) subline += ` · ${parsed.period.toLowerCase()}`
    rows = [
      parsed.accum?.appliesTo && ['Applies to', parsed.accum.appliesTo],
      parsed.accum?.including && ['Member costs that count', parsed.accum.including],
      (parsed.period || parsed.authorization?.period) && [
        'Resets',
        parsed.period || parsed.authorization?.period,
      ],
      parsed.afterMembers && [
        'Family amount starts after',
        `${parsed.afterMembers} member(s) meet the individual deductible`,
      ],
      parsed.waivedIf && ['Waived when', parsed.waivedIf],
      parsed.alsoInclude && ['Also counts', parsed.alsoInclude],
      parsed.label && ['Shown on the plan as', parsed.label],
      parsed.alternateLabels?.length && ['Also shown as', parsed.alternateLabels],
    ]
  } else if (bucket === 'processing') {
    headline = parsed.carveOut
      ? `Handled by ${parsed.carveOut}`
      : parsed.benefitCalcTier
        ? `Paid as ${parsed.benefitCalcTier}`
        : (parsed.repricers?.[0] ? `Priced by ${parsed.repricers[0]}` : 'Processing rule')
    subline = parsed.carveOut
      ? 'This service is sent to an external vendor. Cost share may be defined by that vendor.'
      : parsed.benefitCalcTier
        ? `The claim is paid as if it were on the ${parsed.benefitCalcTier} network.`
        : 'This rule changes how the claim is processed, not the member’s listed cost share.'
    rows = [
      applies && ['Applies to', applies],
      parsed.serviceCodes?.length && ['Service codes', parsed.serviceCodes],
      parsed.repricers?.length && ['Pricing vendor', parsed.repricers],
      parsed.billedOn?.length && ['Claim type', parsed.billedOn.map(titleCaseWord)],
    ]
  } else {
    const pct = parsed.benefitPercent?.display
    const copay = parsed.copay?.text
    const parts = []
    if (pct) parts.push(pct)
    if (copay) parts.push(`${copay} copay`)
    if (who) parts.push(who)
    if (when) parts.push(when)
    if (parsed.unitCap?.text) parts.push(parsed.unitCap.text)
    if (parsed.thenBenefit?.text) parts.push(parsed.thenBenefit.text)
    headline = parts.length
      ? parts.join(' · ')
      : (parsed.isExclusionOverride ? 'Covered even though the base plan excludes it' : parsed.label || 'Covered')
    const story = []
    if (parsed.isExclusionOverride) {
      story.push('A rider covers this even if the base plan excludes it.')
    } else if (parsed.isDefault) {
      story.push('Used only when no more specific benefit matches.')
    }
    if (pct && /%$/.test(pct)) story.push(`The plan pays ${pct} of the allowed amount.`)
    if (copay) story.push(`The member pays a ${copay} copay.`)
    if (parsed.deductibleWaived) story.push('The deductible does not apply.')
    else if (pct || copay) story.push('The deductible still applies unless another rule waives it.')
    if (parsed.thenBenefit?.text) story.push(`After the threshold below, coverage becomes ${parsed.thenBenefit.text.replace(/^Then\s+/i, '')}.`)
    subline = story.join(' ') || null
    const sameBits = [
      parsed.samePractitioner && 'practitioner',
      parsed.sameSupplier && 'supplier',
      parsed.sameDiagnosis && 'diagnosis',
    ].filter(Boolean)
    rows = [
      applies && ['Applies to', applies],
      parsed.serviceCodes?.length && ['Service codes', parsed.serviceCodes],
      parsed.thenBenefit?.until && ['Changes after', parsed.thenBenefit.until],
      parsed.paidAgainst && ['Uses this benefit bucket', parsed.paidAgainst],
      parsed.excludes.length && ['Does not apply when', parsed.excludes],
      parsed.networks.length
        && !parsed.excludes.some((e) => String(e).startsWith('network '))
        && ['Networks', parsed.networks],
      parsed.memberAgeText && ['Member age', parsed.memberAgeText],
      parsed.memberRelationships?.length && ['Who it applies to', parsed.memberRelationships],
      who && ['Clinician', who === 'PCP' ? "The member’s PCP" : who],
      when && ['When', when],
      parsed.sameClaimAs?.length && ['Only with these services on the same claim', parsed.sameClaimAs],
      parsed.sameConfinementAs?.length && ['Only during the same confinement as', parsed.sameConfinementAs],
      parsed.sameDayAs?.length && ['Only on the same day as', parsed.sameDayAs],
      sameBits.length && ['Must be the same', sameBits.join(', ')],
      parsed.carveOut && ['Vendor', parsed.carveOut],
      parsed.claimSources?.length && ['When the claim is submitted as', parsed.claimSources],
      parsed.repricers?.length && ['Pricing vendor', parsed.repricers],
      parsed.billedOn?.length && ['Claim type', parsed.billedOn.map(titleCaseWord)],
      parsed.label && ['Shown on the plan as', parsed.label],
      parsed.alternateLabels?.length && ['Also shown as', parsed.alternateLabels],
      parsed.requiresReview && ['Manual review', 'Required before the claim pays'],
      (parsed.message || parsed.authorization?.message) && [
        'Message on the EOB',
        parsed.message || parsed.authorization?.message,
      ],
    ]
  }

  const detailRows = rows.filter(Boolean)
  const hasAnswer = !!(parsed.hasStructure || headline)

  return (
    <div
      className={`prov summary-card bucket-${bucket}${canOpenCodes ? ' prov-clickable' : ''}`}
      onClick={canOpenCodes ? openPrimaryCodes : undefined}
      onKeyDown={canOpenCodes ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openPrimaryCodes(e)
        }
      } : undefined}
      role={canOpenCodes ? 'button' : undefined}
      tabIndex={canOpenCodes ? 0 : undefined}
    >
      <div className="prov-meta">
        <span className={`badge bucket-${bucket}`}>
          {bucketLabel(bucket, parsed, p.type)}
        </span>
        <span className={`badge ${p.isInNetwork ? 'inn' : 'oon'}`}>
          {p.isInNetwork ? 'In network' : 'Out of network'}
        </span>
        {parsed.deductibleWaived && bucket === 'benefit' && (
          <span className="badge bucket-benefit">Deductible waived</span>
        )}
        {p.slotName && (
          <span className="badge" title={p.weight != null ? `Slot weight ${p.weight}` : undefined}>
            {p.slotName}{p.weight != null ? ` · ${p.weight}` : ''}
          </span>
        )}
        {canOpenCodes && (
          <button
            type="button"
            className="coverage__link-btn prov-codes-link"
            onClick={openPrimaryCodes}
          >
            View codes
          </button>
        )}
      </div>

      <div className="prov-answer">
        {headline && <div className="prov-headline">{headline}</div>}
        {subline && <div className="prov-subline">{subline}</div>}
      </div>

      {!!callouts.length && (
        <ChipList items={callouts} onChipClick={onOpenCodes || undefined} />
      )}

      <ProvisionOrigin sources={provisionSourceList(p)} />

      {hasAnswer ? (
        <SummaryRows rows={detailRows} />
      ) : (
        <div className="prov-text">{parsed.populatedText}</div>
      )}

      {categories.length > 1 && onOpenCodes && (
        <div className="prov-code-cats" onClick={(e) => e.stopPropagation()}>
          <span className="prov-code-cats-label">Service categories</span>
          <ChipList items={categories} onChipClick={onOpenCodes} />
        </div>
      )}

      {parsed.populatedText && hasAnswer && (
        <details className="prov-hrl" onClick={(e) => e.stopPropagation()}>
          <summary>Full rule text</summary>
          <div className="prov-text">{parsed.populatedText}</div>
        </details>
      )}
    </div>
  )
}

const SUMMARY_SECTIONS = [
  { id: 'benefit', title: 'Covered', hint: 'What the plan pays and what the member pays' },
  { id: 'limit', title: 'Limits', hint: 'Visit, day, or dollar maximums' },
  { id: 'exclusion', title: 'Not covered', hint: 'The member pays the full allowed amount' },
  { id: 'authorization', title: 'Prior auth & referrals', hint: 'What must be approved before the claim pays' },
  { id: 'accumulator', title: 'Deductible & out-of-pocket', hint: 'What the member must spend before the plan pays more' },
  { id: 'processing', title: 'Processing', hint: 'Vendors, pricing, and how the claim is handled' },
  { id: 'other', title: 'Other', hint: 'Rules that do not fit the categories above' },
]

function ProvisionSummaryList({
  provisions,
  variableValues,
  focusService = null,
  onOpenCodes = null,
}) {
  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(SUMMARY_SECTIONS.map((s) => [s.id, []]))
    for (const p of provisions || []) {
      const parsed = parseProvision(p, variableValues)
      const bucket = summaryBucket(p, parsed)
      buckets[bucket].push({ p, parsed })
    }
    return buckets
  }, [provisions, variableValues])

  const sections = SUMMARY_SECTIONS.filter((s) => grouped[s.id].length > 0)
  if (!sections.length) return <p className="muted">No matching provisions.</p>

  const benefitHits = grouped.benefit || []
  const glance = benefitHits
    .map(({ parsed }) => parsed.benefitPercent?.display || parsed.copay?.text)
    .filter(Boolean)
  const uniqueGlance = [...new Set(glance)].slice(0, 3)

  return (
    <div className="summary-sections">
      {!!uniqueGlance.length && (
        <p className="summary-glance" role="status">
          <span className="summary-glance-label">Cost share</span>
          <span className="summary-glance-vals">
            {uniqueGlance.map((g) => (
              <span key={g} className="summary-glance-val">{g}</span>
            ))}
          </span>
        </p>
      )}
      {sections.map((section) => (
        <section key={section.id} className={`summary-section section-${section.id}`}>
          <div className="summary-section-head">
            <h3>
              {section.title}
              <span className="badge count">{grouped[section.id].length}</span>
            </h3>
            {section.hint && <p className="summary-section-hint">{section.hint}</p>}
          </div>
          <div className="stack">
            {grouped[section.id].map(({ p, parsed }, i) => (
              <ProvisionSummaryCard
                key={i}
                provision={p}
                parsed={parsed}
                focusService={focusService}
                onOpenCodes={onOpenCodes}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

/* ---- lines 1667-1675 ---- */
function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  const num = Number(n)
  const abs = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  return num < 0 ? `-$${abs}` : `$${abs}`
}

/* ---- lines 2931-3573 ---- */
function networkAccumShort(net) {
  if (!net) return null
  if (net.ded === false && net.oop === false) return 'Neither deductible nor OOP'
  if (net.ded === true && net.oop === true) return 'Deductible + OOP max'
  if (net.ded === false && net.oop === true) return 'OOP max only (no deductible)'
  if (net.ded === true && net.oop === false) return 'Deductible only'
  if (net.oop === true) return 'OOP max'
  if (net.ded === true) return 'Deductible'
  return null
}

function parseAccumAcrossTiers(setup) {
  const raw = String(setup?.rawName || '')
  const dir = (from, to, apiVal) => {
    if (apiVal === true || apiVal === false) return apiVal
    const noFrom = new RegExp(`NO\\s+ACCUMULATION\\s+FROM\\s+${from}\\s+TO\\s+${to}`, 'i')
    const noPhrase = new RegExp(`NO\\s+${from}\\s+ACCUMULATION\\s+TO\\s+${to}`, 'i')
    const yesPhrase = new RegExp(`${from}\\s+ACCUMULATION\\s+TO\\s+${to}`, 'i')
    if (noFrom.test(raw) || noPhrase.test(raw)) return false
    if (yesPhrase.test(raw)) return true
    if (setup?.crossAccumulation === true) return true
    if (setup?.crossAccumulation === false) return false
    return null
  }

  const labels = []
  if (/INDIV(?:IDUAL|DUAL)/i.test(raw)) labels.push('Individual')
  if (/EE\s*\+\s*SP/i.test(raw)) labels.push('EE+SP')
  if (/EE\s*\+\s*CH/i.test(raw)) labels.push('EE+CH')
  if (/\bFAMILY\b/i.test(raw) && !labels.includes('Family')) labels.push('Family')

  const setMatch = raw.match(/Using\s+(\d+)\s*Tier\s+Coverage\s+Tier\s+Set/i)

  return {
    family: setup?.embedded ? 'embedded' : setup?.aggregate ? 'aggregate' : null,
    innToOon: dir('INN', 'OON', setup?.innAccumulatesToOon),
    oonToInn: dir('OON', 'INN', setup?.oonAccumulatesToInn),
    coverageTierSetSize: setup?.coverageTierSetSize ?? (setMatch ? Number(setMatch[1]) : null),
    coverageTierLabels: labels,
  }
}

function flowArrowLabel(on, from, to) {
  if (on === true) return `${from} spend counts toward ${to}`
  if (on === false) return `${from} spend does not count toward ${to}`
  return `${from} → ${to} sharing not specified`
}

function buildAccumStory(setup, flow) {
  if (!setup) return null
  const sentences = []
  if (setup.tiers === 1) {
    sentences.push('One benefit tier — the same cost sharing applies in and out of network.')
  } else if (setup.tiers) {
    sentences.push(`${setup.tiers} benefit tiers (in-network vs out-of-network), each with its own deductible and OOP rules.`)
  }
  if (flow.family === 'embedded') {
    sentences.push('Family deductibles are embedded: each person has an individual deductible, and the family amount is a cap.')
  } else if (flow.family === 'aggregate') {
    sentences.push('Family deductibles are aggregate: the household shares one pool, and benefits generally wait until that family amount is met.')
  }
  if (flow.innToOon === true && flow.oonToInn === true) {
    sentences.push('In-network and out-of-network spend cross-accumulate both ways.')
  } else if (flow.innToOon === true && flow.oonToInn === false) {
    sentences.push('In-network spend counts toward out-of-network limits, but out-of-network spend does not count toward in-network.')
  } else if (flow.innToOon === false && flow.oonToInn === true) {
    sentences.push('Out-of-network spend counts toward in-network limits, but not the other way.')
  } else if (flow.innToOon === false && flow.oonToInn === false) {
    sentences.push('The two networks do not share: an in-network claim does not reduce out-of-network deductible/OOP, and vice versa.')
  }
  const detail = String(setup.rxShareDetail || '').toLowerCase()
  if (detail.includes('ded and oop') || (setup.rxSharedWithMed && detail.includes('shared') && !detail.includes('oop shared'))) {
    sentences.push('Pharmacy deductible and OOP combine with medical into one set of limits.')
  } else if (detail.includes('oop shared')) {
    sentences.push('Pharmacy OOP spends toward the same medical OOP maximum.')
  } else if (detail.includes('not shared') || setup.rxSharedWithMed === false) {
    sentences.push('Pharmacy has its own accumulators and does not share medical deductible/OOP.')
  } else if (setup.rxSharedWithMed === true) {
    sentences.push('Pharmacy shares medical accumulators.')
  }
  if (flow.coverageTierLabels.length) {
    const n = flow.coverageTierSetSize || flow.coverageTierLabels.length
    sentences.push(`Enrollment uses a ${n}-tier coverage set (${flow.coverageTierLabels.join(', ')}).`)
  }
  return sentences.length ? sentences.join(' ') : null
}

function AccumTierAmounts({ data }) {
  const rows = [
    ['Individual deductible', data?.individualDed],
    ['Family deductible', data?.familyDed],
    ['Individual OOP max', data?.individualOop],
    ['Family OOP max', data?.familyOop],
  ].filter(([, v]) => v)
  if (!rows.length) return null
  return (
    <dl className="accum-tier-amounts">
      {rows.map(([label, value]) => (
        <div key={label} className="accum-tier-amount-row">
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function AccumTierColumn({ net, title, applies, amounts }) {
  const short = networkAccumShort(applies)
  return (
    <div className={`accum-tier-col ${net}`}>
      <div className="accum-tier-col-head">
        <h3>{title}</h3>
        {short && <span className={`badge ${net}`}>{short}</span>}
      </div>
      {applies?.ded === false && applies?.oop === false && (
        <p className="muted">No deductible or OOP on this tier.</p>
      )}
      <AccumTierAmounts data={amounts} />
    </div>
  )
}

function AccumSetupSummary({ setup, riders, variableValues, allProvisions }) {
  const flow = useMemo(() => parseAccumAcrossTiers(setup), [setup])
  const story = useMemo(() => buildAccumStory(setup, flow), [setup, flow])
  const [showRules, setShowRules] = useState(false)

  const share = useMemo(() => {
    const provisions = [
      ...(allProvisions || []).filter((p) => /Deductible|OutOfPocket/i.test(p.type || '')),
      ...(riders || []).flatMap((r) => r.dedOopProvisions || []),
    ]
    return buildPlanCostShare(provisions, variableValues, setup)
  }, [allProvisions, riders, variableValues, setup])

  const { innRules, oonRules, total } = useMemo(() => {
    const list = []
    for (const r of riders || []) {
      for (const p of r.dedOopProvisions || []) {
        list.push({ ...p, source: r.name, isInNetwork: p.isInNetwork })
      }
    }
    return {
      innRules: list.filter((p) => p.isInNetwork !== false).slice(0, 8),
      oonRules: list.filter((p) => p.isInNetwork === false).slice(0, 8),
      total: list.length,
    }
  }, [riders])

  const chips = []
  if (setup?.tiers != null) chips.push(`${setup.tiers} benefit tier${setup.tiers === 1 ? '' : 's'}`)
  if (flow.family === 'embedded') chips.push('Embedded family')
  if (flow.family === 'aggregate') chips.push('Aggregate family')
  if (flow.innToOon === true && flow.oonToInn === true) chips.push('Cross accum both ways')
  else if (flow.innToOon === true && flow.oonToInn === false) chips.push('INN → OON only')
  else if (flow.innToOon === false && flow.oonToInn === true) chips.push('OON → INN only')
  else if (flow.innToOon === false && flow.oonToInn === false) chips.push('No cross accum')
  if (setup?.rxSharedWithMed === true || /shared/i.test(setup?.rxShareDetail || '')) chips.push('RX shares medical')
  else if (setup?.rxSharedWithMed === false) chips.push('RX separate')
  if (flow.coverageTierSetSize) chips.push(`${flow.coverageTierSetSize}-tier enrollment`)

  if (!setup && !total) {
    return <p className="muted">No accumulator setup summary available for this plan.</p>
  }

  const showBridge = setup?.tiers !== 1

  return (
    <div className="accum-setup-summary">
      {!!chips.length && (
        <div className="accum-fact-chips">
          {chips.map((c) => (
            <span key={c} className="badge">{c}</span>
          ))}
        </div>
      )}

      {story && <p className="accum-story">{story}</p>}

      {setup && (
        <div className={`accum-tier-flow ${showBridge ? '' : 'single'}`.trim()}>
          <AccumTierColumn
            net="inn"
            title="In network"
            applies={setup.inn}
            amounts={share.medical?.inn}
          />
          {showBridge && (
            <div className="accum-flow-bridge" aria-label="How the tiers share">
              <div className={`accum-flow-arrow ${flow.innToOon === false ? 'off' : flow.innToOon === true ? 'on' : ''}`}>
                <span className="accum-flow-glyph" aria-hidden>{flow.innToOon === false ? '↛' : '→'}</span>
                <span>{flowArrowLabel(flow.innToOon, 'INN', 'OON')}</span>
              </div>
              <div className={`accum-flow-arrow reverse ${flow.oonToInn === false ? 'off' : flow.oonToInn === true ? 'on' : ''}`}>
                <span className="accum-flow-glyph" aria-hidden>{flow.oonToInn === false ? '↛' : '←'}</span>
                <span>{flowArrowLabel(flow.oonToInn, 'OON', 'INN')}</span>
              </div>
            </div>
          )}
          <AccumTierColumn
            net="oon"
            title="Out of network"
            applies={setup.oon}
            amounts={share.medical?.oon}
          />
        </div>
      )}

      {share.rx && (
        <div className="accum-rx-note">
          <h3>Pharmacy</h3>
          <CostShareMatrix data={share.rx} />
        </div>
      )}
      {share.rxSameAsMed && (
        <p className="muted cost-share-note">Pharmacy uses the same deductible and OOP amounts as medical.</p>
      )}

      {!!flow.coverageTierLabels.length && (
        <div className="accum-enroll-tiers">
          <span className="accum-enroll-label">Enrollment tiers</span>
          <div className="network-sn-chips">
            {flow.coverageTierLabels.map((t) => (
              <span key={t} className="chip static">{t}</span>
            ))}
          </div>
        </div>
      )}

      {!!total && (
        <div className="accum-setup-dedoop">
          <button
            type="button"
            className="coverage__link-btn"
            onClick={() => setShowRules((v) => !v)}
          >
            {showRules ? 'Hide' : 'Show'} deductible &amp; OOP rule text
            {' '}({total})
          </button>
          {showRules && (
            <div className="inn-oon-grid accum-rules-grid">
              <div className="inn-oon-col inn">
                <div className="inn-oon-head">
                  <span className="badge inn">In network</span>
                  <span className="badge count inn">{innRules.length}</span>
                </div>
                {innRules.length ? (
                  <ProvisionSummaryList provisions={innRules} variableValues={variableValues || {}} />
                ) : (
                  <p className="muted">No in-network deductible / OOP rules.</p>
                )}
              </div>
              <div className="inn-oon-col oon">
                <div className="inn-oon-head">
                  <span className="badge oon">Out of network</span>
                  <span className="badge count oon">{oonRules.length}</span>
                </div>
                {oonRules.length ? (
                  <ProvisionSummaryList provisions={oonRules} variableValues={variableValues || {}} />
                ) : (
                  <p className="muted">No out-of-network deductible / OOP rules.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!!riders?.length && (
        <details className="accum-rider-source">
          <summary>Accumulator rider</summary>
          {riders.map((r) => (
            <div key={r.key} className="accum-rider-source-body">
              <div className="name">{r.name}</div>
              {r.hccId && <div className="sub mono">{r.hccId}</div>}
            </div>
          ))}
        </details>
      )}
    </div>
  )
}

/**
 * Pull deductible / OOP amounts into a CSR-friendly matrix from provisions + variables.
 */
function buildPlanCostShare(provisions, variableValues = {}, setup = null) {
  const empty = () => ({
    individualDed: null,
    familyDed: null,
    individualOop: null,
    familyOop: null,
  })
  const medical = { inn: empty(), oon: empty() }
  const rx = { inn: empty(), oon: empty() }

  const put = (bucket, net, level, kind, display) => {
    if (!display) return
    const cell = bucket[net]
    if (!cell) return
    const key = kind === 'Deductible'
      ? (level === 'family' ? 'familyDed' : 'individualDed')
      : (level === 'family' ? 'familyOop' : 'individualOop')
    if (!cell[key]) cell[key] = display
  }

  for (const p of provisions || []) {
    const parsed = parseProvision(p, variableValues)
    const a = parsed.accum
    if (!a?.display) continue
    const net = p.isInNetwork === false || /out\s*of\s*network/i.test(p.tierName || '')
      ? 'oon'
      : 'inn'
    const level = String(a.level || '').toLowerCase().startsWith('fam') ? 'family' : 'individual'
    const kind = /out-of-pocket|oop/i.test(a.kind || '') ? 'oop' : 'Deductible'
    put(a.isRx ? rx : medical, net, level, kind, a.display)
  }

  // Fallback: common VARIABLE_VALUE keys when provisions didn't resolve
  const v = (k) => variableValues?.[k]?.display || null
  if (!medical.inn.individualDed) medical.inn.individualDed = v('INN Individual Deductible')
  if (!medical.inn.familyDed) medical.inn.familyDed = v('INN Family Deductible')
  if (!medical.inn.individualOop) medical.inn.individualOop = v('INN Individual OOP Max')
  if (!medical.inn.familyOop) medical.inn.familyOop = v('INN Family OOP Max')
  if (!medical.oon.individualDed) medical.oon.individualDed = v('OON Individual Deductible')
  if (!medical.oon.familyDed) medical.oon.familyDed = v('OON Family Deductible')
  if (!medical.oon.individualOop) medical.oon.individualOop = v('OON Individual OOP Max')
  if (!medical.oon.familyOop) medical.oon.familyOop = v('OON Family OOP Max')
  if (!rx.inn.individualDed) rx.inn.individualDed = v('INN Individual RX Deductible')
  if (!rx.inn.familyDed) rx.inn.familyDed = v('INN Family RX Deductible')
  if (!rx.inn.individualOop) rx.inn.individualOop = v('INN Individual RX OOP Max')
  if (!rx.inn.familyOop) rx.inn.familyOop = v('INN Family RX OOP Max')
  if (!rx.oon.individualDed) rx.oon.individualDed = v('OON Individual RX Deductible')
  if (!rx.oon.familyDed) rx.oon.familyDed = v('OON Family RX Deductible')
  if (!rx.oon.individualOop) rx.oon.individualOop = v('OON Individual RX OOP Max')
  if (!rx.oon.familyOop) rx.oon.familyOop = v('OON Family RX OOP Max')

  const hasAny = (side) => Object.values(side.inn).some(Boolean) || Object.values(side.oon).some(Boolean)
  const rxSameAsMed = hasAny(rx) && ['inn', 'oon'].every((net) => (
    ['individualDed', 'familyDed', 'individualOop', 'familyOop'].every(
      (k) => !rx[net][k] || rx[net][k] === medical[net][k]
    )
  ))

  const chips = []
  if (setup?.embedded) chips.push('Embedded')
  else if (setup?.aggregate) chips.push('Aggregate')
  if (setup?.tiers != null) chips.push(`${setup.tiers} tier${setup.tiers === 1 ? '' : 's'}`)
  if (setup?.crossAccumulation === true) chips.push('Cross accumulation')
  else if (setup?.crossAccumulation === false) chips.push('No cross accum')
  if (setup?.rxSharedWithMed === true || /shared/i.test(setup?.rxShareDetail || '')) {
    chips.push('RX shares medical')
  } else if (setup?.rxSharedWithMed === false) {
    chips.push('RX separate')
  }

  return {
    medical,
    rx: hasAny(rx) && !rxSameAsMed ? rx : null,
    rxSameAsMed: hasAny(rx) && rxSameAsMed,
    chips,
    hasMedical: hasAny(medical),
  }
}

function CostShareMatrix({ title, data }) {
  if (!data) return null
  const rows = [
    {
      label: 'Individual deductible',
      inn: data.inn.individualDed,
      oon: data.oon.individualDed,
    },
    {
      label: 'Family deductible',
      inn: data.inn.familyDed,
      oon: data.oon.familyDed,
    },
    {
      label: 'Individual OOP max',
      inn: data.inn.individualOop,
      oon: data.oon.individualOop,
    },
    {
      label: 'Family OOP max',
      inn: data.inn.familyOop,
      oon: data.oon.familyOop,
    },
  ].filter((r) => r.inn || r.oon)

  if (!rows.length) return null

  return (
    <div className="cost-share-block">
      {title && <h3 className="cost-share-block-title">{title}</h3>}
      <div className="table-wrapper cost-share-table-wrap">
        <table className="cost-share-table">
          <thead>
            <tr>
              <th>Limit</th>
              <th className="cost-share-num">In network</th>
              <th className="cost-share-num">Out of network</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="cost-share-num">{r.inn || '—'}</td>
                <td className="cost-share-num">{r.oon || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PlanCostShareOverview({ provisions, variableValues, setup }) {
  const share = useMemo(
    () => buildPlanCostShare(provisions, variableValues, setup),
    [provisions, variableValues, setup]
  )

  if (!share.hasMedical && !share.rx) {
    return (
      <p className="muted">
        No deductible / OOP amounts found on this plan yet. Check Accumulator setup below.
      </p>
    )
  }

  return (
    <div className="plan-cost-share">
      {!!share.chips.length && (
        <div className="plan-cost-share-chips">
          {share.chips.map((c) => (
            <span key={c} className="badge">{c}</span>
          ))}
        </div>
      )}
      <div className={`cost-share-grid ${share.rx ? 'has-rx' : ''}`}>
        <CostShareMatrix title="Medical" data={share.medical} />
        {share.rx && <CostShareMatrix title="Pharmacy (RX)" data={share.rx} />}
      </div>
      {share.rxSameAsMed && (
        <p className="muted cost-share-note">
          Pharmacy uses the same deductible and OOP amounts as medical.
        </p>
      )}
    </div>
  )
}

function PremiumTierOverview({ plan, premiums }) {
  const tierSet = plan?.coverageTierSet || null
  const tiers = premiums?.tiers || []
  const schedule = premiums?.schedule || null
  const tierSetName = tierSet?.name || schedule?.tierSetName || null

  const showMoneyCols = useMemo(() => {
    const hasPspm = tiers.some((t) => t.pspm != null && Number(t.pspm) !== 0)
    const hasPmpm = tiers.some((t) => t.pmpm != null && Number(t.pmpm) !== 0)
    const hasFlat = tiers.some((t) => t.flat != null && Number(t.flat) !== 0)
    return { hasPspm, hasPmpm, hasFlat }
  }, [tiers])

  if (!tierSetName && !tiers.length && !tierSet?.description) {
    return (
      <p className="muted">
        No coverage tier set or billing premiums found for this account and plan as of this date.
      </p>
    )
  }

  return (
    <div className="premium-tier-overview">
      <div className="premium-tier-meta">
        {tierSetName && (
          <div className="premium-tier-set">
            <span className="premium-tier-set-label">Coverage tier set</span>
            <span className="premium-tier-set-name">{tierSetName}</span>
            {tierSet?.statusLabel && (
              <StatusBadge status={tierSet.status} label={tierSet.statusLabel} />
            )}
          </div>
        )}
        {schedule?.effective && (
          <p className="muted premium-tier-schedule">
            Rate schedule {schedule.effective}
            {schedule.expires ? ` → ${schedule.expires}` : ' → open'}
            {schedule.statusLabel ? ` · ${schedule.statusLabel}` : ''}
          </p>
        )}
        {tierSet?.description && (
          <p className="muted premium-tier-desc">{tierSet.description}</p>
        )}
      </div>

      {!!tiers.length && (
        <div className="table-wrapper cost-share-table-wrap">
          <table className="cost-share-table">
            <thead>
              <tr>
                <th>Coverage tier</th>
                {showMoneyCols.hasPspm && <th className="cost-share-num">PSPM</th>}
                {showMoneyCols.hasPmpm && <th className="cost-share-num">PMPM</th>}
                {showMoneyCols.hasFlat && <th className="cost-share-num">Flat</th>}
                <th className="cost-share-num">Package</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.key != null ? `k:${t.key}` : t.name}>
                  <td>{t.name}</td>
                  {showMoneyCols.hasPspm && (
                    <td className="cost-share-num">{formatMoney(t.pspm)}</td>
                  )}
                  {showMoneyCols.hasPmpm && (
                    <td className="cost-share-num">{formatMoney(t.pmpm)}</td>
                  )}
                  {showMoneyCols.hasFlat && (
                    <td className="cost-share-num">{formatMoney(t.flat)}</td>
                  )}
                  <td className="cost-share-num">{formatMoney(t.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!tiers.length && tierSetName && (
        <p className="muted">
          Tier set is configured, but no account billing rates were found for this plan as of this date.
        </p>
      )}
    </div>
  )
}

function RepricerSection({ repricers, openRiderModal }) {
  if (!repricers?.length) {
    return (
      <p className="muted">
        No repricer riders on this plan. Repricers set how out-of-network or wrap-network claims are priced (e.g. Cigna, MultiPlan).
      </p>
    )
  }
  return (
    <div className="repricer-list">
      <p className="muted repricer-intro">
        These riders send eligible claims to an external pricing vendor before benefits are applied.
      </p>
      {repricers.map((r) => (
        <button
          key={r.key}
          type="button"
          className="repricer-card"
          onClick={() => openRiderModal?.(r.key)}
        >
          <div className="repricer-card-main">
            <div className="name">{r.name}</div>
            <div className="sub">{r.summary || r.description || r.typeName || 'Repricer'}</div>
          </div>
          <div className="repricer-card-meta">
            {r.vendor && <span className="badge claim-status claim-status-repricing">{r.vendor}</span>}
            <StatusBadge status={r.status} label={r.statusLabel} />
          </div>
        </button>
      ))}
    </div>
  )
}

function networkDefLookup(rider) {
  if (rider?.networkDefCompKey != null) {
    return { networkDefCompKey: Number(rider.networkDefCompKey) }
  }
  const name = rider?.networkDefinition?.name || rider?.name
  if (name) return { networkDefCompName: name }
  return null
}

function oxfordJoin(items) {
  if (!items.length) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

function uniqueBenefitNetworks(tiers) {
  const seen = new Set()
  const out = []
  for (const tier of tiers || []) {
    for (const n of tier.benefitNetworks || []) {
      const id = String(n.benefitNetworkKey ?? n.benefitNetworkName ?? '')
      if (!id || seen.has(id)) continue
      seen.add(id)
      out.push(n)
    }
  }
  return out
}

function benefitNetworkIds(networks) {
  const ids = new Set()
  for (const n of networks || []) {
    if (n.benefitNetworkName != null) ids.add(String(n.benefitNetworkName))
    if (n.benefitNetworkKey != null) ids.add(String(n.benefitNetworkKey))
  }
  return ids
}

function mergeNetworkView(rider, details) {
  const tiers = details?.tiers?.length ? details.tiers : []
  const benefitNetworks = uniqueBenefitNetworks(tiers)
  const ids = benefitNetworkIds(benefitNetworks)
  const sn = rider.supplierNetworks || []
  // Rider text often lists benefit-network IDs (201, 309) as if they were
  // supplier-network keys. Prefer the details API roster and only keep
  // suppliers that are not those IDs.
  const resolved = sn.filter((s) => !s.unresolved && !ids.has(String(s.key)))
  const unresolved = sn.filter((s) => s.unresolved && !ids.has(String(s.key)))
  return { tiers, benefitNetworks, resolved, unresolved, sn }
}

function benefitNetworkLabel(n) {
  return n.benefitNetworkDesc || n.benefitNetworkName || 'Benefit network'
}

function networkVersionLabel(eff, exp) {
  if (!eff) return null
  const expYear = Number(String(exp || '').slice(0, 4))
  const open = !exp || Number.isNaN(expYear) || expYear >= 2999
  return open ? `Effective ${eff}` : `${eff} → ${exp}`
}

function buildNetworkStory(rider, view) {
  const sentences = []
  const names = view.benefitNetworks.map(benefitNetworkLabel)
  if (names.length) {
    sentences.push(
      `In-network care is defined by ${names.length} benefit network${names.length === 1 ? '' : 's'}: ${oxfordJoin(names)}.`
    )
  } else if (view.resolved.length) {
    const labels = view.resolved.map((s) => s.hccId || s.name).filter(Boolean)
    sentences.push(
      `This Network Definition lists ${view.resolved.length} supplier network${view.resolved.length === 1 ? '' : 's'}${labels.length ? ` (${oxfordJoin(labels)})` : ''} as in-network.`
    )
  } else {
    sentences.push('This Network Definition rider decides which networks count as in-network for members.')
  }

  const flags = view.tiers.reduce((acc, t) => ({
    self: acc.self || !!t.useForSelfReferral,
    promo: acc.promo || !!t.useForInNetworkPromotion,
    selfOff: acc.selfOff || t.useForSelfReferral === false,
    promoOff: acc.promoOff || t.useForInNetworkPromotion === false,
  }), { self: false, promo: false, selfOff: false, promoOff: false })

  if (flags.self) {
    sentences.push('Members may self-refer to in-network specialists without a PCP referral.')
  } else if (flags.selfOff) {
    sentences.push('Self-referral to in-network specialists is not enabled on this definition.')
  }
  if (flags.promo) {
    sentences.push('INN promotion is on: eligible wrap or NSA claims can pay at in-network cost share.')
  } else if (flags.promoOff) {
    sentences.push('INN promotion is off: wrap or NSA claims do not automatically get in-network cost share.')
  }
  if (view.resolved.length && names.length) {
    const labels = view.resolved.map((s) => s.hccId || s.name).filter(Boolean)
    sentences.push(`Supplier network roster includes ${oxfordJoin(labels)}.`)
  }
  return sentences.join(' ')
}

function NetworkRosterTable({ rows, idLabel, nameLabel }) {
  if (!rows?.length) return null
  return (
    <div className="table-wrapper cost-share-table-wrap">
      <table className="cost-share-table network-roster-table">
        <thead>
          <tr>
            <th>{idLabel}</th>
            <th>{nameLabel}</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.id}</td>
              <td>{r.name || '—'}</td>
              <td>
                {r.status || r.statusLabel
                  ? <StatusBadge status={r.status} label={r.statusLabel} />
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function mapBenefitNetworkRows(networks) {
  return (networks || []).map((n) => ({
    id: n.benefitNetworkName || n.benefitNetworkKey,
    name: n.benefitNetworkDesc && n.benefitNetworkDesc !== n.benefitNetworkName
      ? n.benefitNetworkDesc
      : (n.benefitNetworkDesc || n.benefitNetworkName),
    status: n.benefitNetworkStatus,
    statusLabel: n.benefitNetworkStatusLabel,
  }))
}

function NetworkRiderCard({ rider, asOf, openRiderModal }) {
  const lookup = networkDefLookup(rider)
  const { data, isLoading, error } = useQuery({
    queryKey: ["network-def-comp-details", lookup, asOf],
    queryFn: () => fetchNetworkDefCompDetails({ ...lookup, asOfDate: asOf }),
    enabled: !!asOf && !!lookup,
    staleTime: 60_000,
    retry: false,
  })
  const details = data && !data.errorMessage ? data : null
  const view = useMemo(() => mergeNetworkView(rider, details), [rider, details])
  const story = useMemo(() => buildNetworkStory(rider, view), [rider, view])

  const primaryTier = view.tiers[0] || null
  const selfRef = view.tiers.some((t) => t.useForSelfReferral)
  const innPromo = view.tiers.some((t) => t.useForInNetworkPromotion)
  const selfKnown = view.tiers.some((t) => t.useForSelfReferral === true || t.useForSelfReferral === false)
  const promoKnown = view.tiers.some((t) => t.useForInNetworkPromotion === true || t.useForInNetworkPromotion === false)
  const version = networkVersionLabel(details?.versionEffDate, details?.versionExpDate)
  const desc = details?.networkDefCompDesc || rider.description
  const showWho = view.resolved.length > 0 || view.unresolved.length > 0
  const showHow = view.benefitNetworks.length > 0
  const showBridge = showWho && showHow

  const chips = []
  if (view.benefitNetworks.length) {
    chips.push(`${view.benefitNetworks.length} benefit network${view.benefitNetworks.length === 1 ? '' : 's'}`)
  }
  if (view.resolved.length) {
    chips.push(`${view.resolved.length} supplier network${view.resolved.length === 1 ? '' : 's'}`)
  }
  if (selfRef) chips.push('Self-referral')
  else if (selfKnown) chips.push('No self-referral')
  if (innPromo) chips.push('INN promotion')
  else if (promoKnown) chips.push('No INN promotion')
  if (details?.networkDefCompStatusLabel || rider.statusLabel) {
    chips.push(details?.networkDefCompStatusLabel || rider.statusLabel)
  }
  if (version) chips.push(version)

  return (
    <div className="accum-setup-summary network-setup-summary">
      {!!chips.length && (
        <div className="accum-fact-chips">
          {chips.map((c) => (
            <span key={c} className="badge">{c}</span>
          ))}
        </div>
      )}

      {story && <p className="accum-story">{story}</p>}

      {isLoading && (
        <div aria-busy="true" aria-label="Loading network definition details">
          <Skeleton width="70%" height="0.9rem" className="mb-sm" />
          <Skeleton width="100%" height="6rem" />
        </div>
      )}

      {(error || data?.errorMessage) && !isLoading && (
        <p className="muted">
          Could not load network definition details
          {data?.errorMessage ? `: ${data.errorMessage}` : ''}.
          Showing what is on the rider.
        </p>
      )}

      {(showWho || showHow) && !isLoading && (
        <div className={`accum-tier-flow ${showBridge ? '' : 'single'}`.trim()}>
          {showWho && (
            <div className="accum-tier-col who">
              <div className="accum-tier-col-head">
                <h3>Who is contracted</h3>
                <span className="badge">{view.resolved.length || view.unresolved.length}</span>
              </div>
              <p className="muted">Supplier networks — provider rosters this plan treats as in-network.</p>
              <NetworkRosterTable
                idLabel="HCC / key"
                nameLabel="Supplier network"
                rows={view.resolved.map((s) => ({
                  id: s.hccId || s.key,
                  name: s.name,
                  status: s.status,
                  statusLabel: s.statusLabel,
                }))}
              />
              {!!view.unresolved.length && (
                <p className="muted">
                  {view.unresolved.length} roster key{view.unresolved.length === 1 ? '' : 's'} could not be resolved
                  {' '}({view.unresolved.map((s) => s.key).join(', ')}).
                </p>
              )}
            </div>
          )}

          {showBridge && (
            <div className="accum-flow-bridge" aria-label="How the definition treats INN">
              <div className={`accum-flow-arrow ${selfRef ? 'on' : selfKnown ? 'off' : ''}`}>
                <span className="accum-flow-glyph" aria-hidden>{selfRef ? '→' : '↛'}</span>
                <span>{selfRef ? 'Members may self-refer' : 'Self-referral is off'}</span>
              </div>
              <div className={`accum-flow-arrow reverse ${innPromo ? 'on' : promoKnown ? 'off' : ''}`}>
                <span className="accum-flow-glyph" aria-hidden>{innPromo ? '←' : '↛'}</span>
                <span>{innPromo ? 'INN promotion (wrap/NSA can pay INN)' : 'INN promotion is off'}</span>
              </div>
            </div>
          )}

          {showHow && (
            <div className="accum-tier-col how">
              <div className="accum-tier-col-head">
                <h3>How the member pays</h3>
                <span className="badge inn">{view.benefitNetworks.length}</span>
              </div>
              <p className="muted">Benefit networks — plan INN buckets for deductibles, copays, and OOP.</p>
              {view.tiers.length > 1 ? view.tiers.map((tier) => (
                <div key={tier.inNtwkTierDefFactKey || tier.tierName} className="network-tier-block">
                  <div className="network-tier-head">
                    <strong>{tier.tierName || 'In-network tier'}</strong>
                    {tier.useForSelfReferral && <span className="chip static">Self-referral</span>}
                    {tier.useForInNetworkPromotion && <span className="chip static">INN promotion</span>}
                  </div>
                  {tier.description && <p className="muted">{tier.description}</p>}
                  <NetworkRosterTable
                    idLabel="ID"
                    nameLabel="Benefit network"
                    rows={mapBenefitNetworkRows(tier.benefitNetworks)}
                  />
                </div>
              )) : (
                <NetworkRosterTable
                  idLabel="ID"
                  nameLabel="Benefit network"
                  rows={mapBenefitNetworkRows(view.benefitNetworks)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {!showHow && !showWho && !isLoading && !!rider.tiers?.length && (
        <p className="muted">
          Benefit tier{rider.tiers.length > 1 ? 's' : ''}: {rider.tiers.map((t) => t.name).join(', ')}
        </p>
      )}

      {(selfKnown || promoKnown) && (
        <div className="network-flag-grid">
          {selfKnown && (
            <div>
              <strong>Self-referral {selfRef ? 'on' : 'off'}</strong>
              <p>
                {selfRef
                  ? 'Members can see in-network specialists without a PCP referral.'
                  : 'In-network specialist visits generally need a referral.'}
              </p>
            </div>
          )}
          {promoKnown && (
            <div>
              <strong>INN promotion {innPromo ? 'on' : 'off'}</strong>
              <p>
                {innPromo
                  ? 'Eligible wrap or NSA claims can pay at in-network cost share.'
                  : 'Wrap or NSA claims do not automatically get in-network cost share.'}
              </p>
            </div>
          )}
        </div>
      )}

      {!!rider.features?.length && (
        <div className="accum-enroll-tiers">
          <span className="accum-enroll-label">Definition flags</span>
          <div className="network-sn-chips">
            {rider.features.map((f) => (
              <span key={f} className="chip static">{f}</span>
            ))}
          </div>
        </div>
      )}

      <details className="accum-rider-source">
        <summary>Network Definition rider</summary>
        <div className="accum-rider-source-body">
          <div className="name">{details?.networkDefCompName || rider.name}</div>
          {desc && <div className="sub">{desc}</div>}
          {primaryTier?.description && primaryTier.description !== desc && (
            <div className="sub">{primaryTier.description}</div>
          )}
          {version && <div className="sub">{version}</div>}
          <StatusBadge
            status={details?.networkDefCompStatus || rider.status}
            label={details?.networkDefCompStatusLabel || rider.statusLabel}
          />
          {openRiderModal && (
            <button
              type="button"
              className="coverage__link-btn"
              onClick={() => openRiderModal(rider.key)}
            >
              Rider provisions
            </button>
          )}
        </div>
      </details>
    </div>
  )
}

function NetworkSection({ networkRiders, benefitNetworks, openRiderModal, asOf }) {
  if (!networkRiders?.length && !benefitNetworks?.length) {
    return (
      <>
        <NetworkWiringGuide highlight="plan" compact />
        <p className="muted" style={{ marginTop: '0.85rem' }}>
          No Network Definition riders on this plan. Those riders list which networks
          count as in-network for members.
        </p>
      </>
    )
  }
  return (
    <div className="network-setup">
      <NetworkWiringGuide highlight="plan" compact />
      {(networkRiders || []).map((r) => (
        <NetworkRiderCard
          key={r.key}
          rider={r}
          asOf={asOf}
          openRiderModal={openRiderModal}
        />
      ))}
      {!networkRiders?.length && !!benefitNetworks?.length && (
        <div className="row-list network-benefit-fallback">
          <p className="muted overview-tab-hint">
            Linked benefit networks (plan INN buckets), if present on the member-plan map.
          </p>
          {benefitNetworks.map((n) => (
            <div className="row" key={n.key}>
              <div>
                <div className="name">{n.name}</div>
                {!!n.supplierNetworks?.length && (
                  <div className="sub">{n.supplierNetworks.map((s) => s.name).join(', ')}</div>
                )}
              </div>
              <StatusBadge status={n.status} label={n.statusLabel} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AccumSetupCard({ setup, riders, variableValues, allProvisions }) {
  if (!setup && !riders?.length) {
    return <p className="muted">No accumulator-setup riders on this plan.</p>
  }

  return (
    <div className="accum-setup">
      <AccumSetupSummary
        setup={setup}
        riders={riders}
        variableValues={variableValues}
        allProvisions={allProvisions}
      />
    </div>
  )
}

function ProvisionModal({ title, subtitle, provisions, components, loading, onClose, variableValues }) {
  const [view, setView] = useState('summary')
  const allProvisions = useMemo(() => {
    if (provisions?.length) return provisions
    const list = []
    for (const c of components || []) {
      for (const p of c.provisions || []) {
        list.push({ ...p, source: c.name })
      }
    }
    return list
  }, [provisions, components])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <Modal isOpen onClose={onClose} className="coverage-provision-modal">
      <div className="coverage-modal-inner" role="dialog" aria-modal="true" aria-label={title || 'Loading'}>
        <div className="coverage-modal-head">
          {loading ? (
            <>
              <Skeleton width="16rem" height="1.25rem" className="mb-sm" />
              <Skeleton width="10rem" height="0.8rem" />
            </>
          ) : (
            <>
              <h2>{title}</h2>
              {subtitle && <p className="muted">{subtitle}</p>}
            </>
          )}
        </div>

        {!loading && (
          <div className="coverage-modal-toolbar">
            <div className="service-subtabs">
              <span className="service-subtab-label">View</span>
              <div className="service-subtab-btns" role="tablist">
                <button
                  type="button"
                  className={view === 'summary' ? 'on' : ''}
                  onClick={() => setView('summary')}
                >
                  Summary
                </button>
                <button
                  type="button"
                  className={view === 'provisions' ? 'on' : ''}
                  onClick={() => setView('provisions')}
                >
                  Provisions
                </button>
              </div>
            </div>
            {!!components?.length && (
              <p className="muted coverage-modal-comp-count">
                {components.length} component{components.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
        )}

        <div className="coverage-modal-body">
          {loading ? (
            <ModalSkeleton />
          ) : view === 'summary' ? (
            <ProvisionSummaryList provisions={allProvisions} variableValues={variableValues || {}} />
          ) : (
            <ProvisionList provisions={allProvisions} />
          )}
        </div>
      </div>
    </Modal>
  )
}

const listKey = (values) => [...(values || [])].map(String).sort().join('|')

/** Strip HRP Default/value suffix so "INN Coins" and "INN Coins(100(Default))" match. */
function normalizeVarName(name) {
  return String(name || '')
    .replace(/\([^()]*\(\s*Default\s*\)\)\s*$/i, '')
    .replace(/\(\$?[\d,.]+(?:\s*\(\s*Default\s*\))?\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const COMPANION_CATEGORY = /place of service|classification and specialty|^after hours$/i

/** PCP / OBGYN / Specialist when the rule is scoped to a clinician type. */
function provisionWhoLabel(parsed) {
  if (parsed?.renderedByPcp) return 'PCP'
  const spec = (parsed?.categories || []).find((c) => /classification and specialty/i.test(c))
  if (spec) return spec.replace(/\s*-\s*Classification and Specialty/i, '').trim()
  const fromVar = normalizeVarName(parsed?.benefitPercent?.name || parsed?.copay?.name || '')
  const m = fromVar.match(/(?:clinic visit|office visit|office surgery physician)\s+([a-z0-9/+-]+)\s+coins/)
  if (!m) return null
  const word = m[1]
  if (/^pcp$/.test(word)) return 'PCP'
  if (/^ob-?gyn$/.test(word)) return 'OBGYN'
  if (/^specialist$/.test(word)) return 'Specialist'
  return word.replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Emergent vs non-emergent when the rule is gated on Emergent - Diagnosis. */
function provisionWhenLabel(parsed) {
  const excl = parsed?.excludes || []
  if (excl.some((e) => /emergent/i.test(e))) return 'Non-emergent'
  const cats = parsed?.categories || []
  if (cats.some((c) => /emergent\s*-\s*diagnosis/i.test(c))) return 'Emergent'
  const fromVar = normalizeVarName(parsed?.benefitPercent?.name || '')
  if (/non\s*emergent/i.test(fromVar)) return 'Non-emergent'
  return null
}

/** Same cost-share answer after VARIABLE_VALUE resolve — ignores HRL wording / Default suffixes. */
function provisionAnswerKey(p, parsed) {
  const net = p.isInNetwork === false ? 'oon' : p.isInNetwork ? 'inn' : 'any'
  return [
    summaryBucket(p, parsed),
    net,
    normalizeVarName(parsed.benefitPercent?.name),
    parsed.benefitPercent?.display || '',
    normalizeVarName(parsed.copay?.name),
    parsed.copay?.text || '',
    parsed.limit?.text || '',
    parsed.unitCap?.text || '',
    parsed.accum?.display || parsed.accum?.text || '',
    parsed.deductibleWaived ? '1' : '0',
    parsed.isExclusion ? '1' : '0',
    parsed.label || '',
    provisionWhoLabel(parsed) || '',
    provisionWhenLabel(parsed) || '',
    parsed.authorization?.required ? 'auth' : '',
    parsed.authorization?.waived ? 'authoff' : '',
    parsed.memberAgeText || '',
    parsed.renderedByPcp ? 'pcp' : '',
    parsed.samePractitioner ? 'prac' : '',
    parsed.sameSupplier ? 'sup' : '',
    listKey(parsed.excludes),
    listKey(parsed.networks),
    listKey(parsed.memberRelationships),
    listKey(parsed.unlessConditions),
  ].join('\0')
}

function collapseEquivalentProvisions(provisions, variableValues) {
  const best = new Map()
  for (const p of provisions || []) {
    const parsed = parseProvision(p, variableValues)
    const key = provisionAnswerKey(p, parsed)
    const prev = best.get(key)
    const sources = mergeProvisionSources(prev?.sources, prev?.source, p.sources, p.source)
    if (!prev) {
      best.set(key, { ...p, sources, source: sources[0]?.name || p.source })
      continue
    }
    const score = (row) => {
      const text = row.text || ''
      return (
        (/\(\s*Default\s*\)/i.test(text) ? 0 : 2)
        + (row.isInNetwork != null ? 1 : 0)
        + (row.type ? 1 : 0)
      )
    }
    const keep = score(p) > score(prev) ? p : prev
    best.set(key, { ...keep, sources, source: sources[0]?.name || keep.source })
  }
  return [...best.values()]
}

function extractServiceCategoryNames(provisions) {
  const set = new Set()
  for (const p of provisions || []) {
    const text = p.text || ''
    const re = /service\s+category\s+(?:"([^"]+)"|'([^']+)')((?:\s*,\s*(?:"([^"]+)"|'([^']+)'))*)/gi
    let m
    while ((m = re.exec(text)) !== null) {
      if (m[1] || m[2]) set.add(m[1] || m[2])
      if (m[3]) {
        for (const q of m[3].matchAll(/"([^"]+)"|'([^']+)'/g)) {
          if (q[1] || q[2]) set.add(q[1] || q[2])
        }
      }
    }
    if (p.serviceCategory) set.add(p.serviceCategory)
  }
  return [...set]
}

/* ---- lines 3575-4068 ---- */
function PlanView({
  coverage,
  asOf,
  answerFilter,
  setAnswerFilter,
  planTab,
  setPlanTab,
  serviceQuery,
  setServiceQuery,
  selectedService,
  setSelectedService,
  riderTab,
  setRiderTab,
  availableRiders,
  riderQ,
  setRiderQ,
  loadAvailableRiders,
  ridersLoading,
  openRiderModal,
  provisionModal,
  setProvisionModal,
  accumulatorSetup,
  allProvisions,
  onSelectVersion,
  askPrefill,
  setAskPrefill,
}) {
  const [coverageView, setCoverageView] = useState('summary')
  const [overviewTab, setOverviewTab] = useState('accumulators')
  const [versionsOpen, setVersionsOpen] = useState(false)
  const [premiumsOpen, setPremiumsOpen] = useState(false)
  const [serviceNavOpen, setServiceNavOpen] = useState(true)
  const [expandedServiceGroups, setExpandedServiceGroups] = useState(() => new Set(['benefit']))
  const [focusCategory, setFocusCategory] = useState(null)
  const [browseReturn, setBrowseReturn] = useState(null)
  const askHistoryPushed = useRef(false)
  const pendingTabAfterPop = useRef(null)
  const askReturnTab = useRef('services')
  const [codesVisible, setCodesVisible] = useState(false)
  const [codesCategory, setCodesCategory] = useState(null)
  const [codesByService, setCodesByService] = useState({})
  // Keep the Ask panel alive once opened so hopping back to Browse to look up
  // another code does not throw away the answer and the typed-in member.
  const [askMounted, setAskMounted] = useState(false)
  const variableValues = coverage.variableValues || {}

  const activeCodesCategory = codesCategory || focusCategory || null
  const codesCacheKey = activeCodesCategory && asOf ? `${activeCodesCategory}::${asOf}` : null
  const codesCacheEntry = codesCacheKey ? codesByService[codesCacheKey] : null

  const onCodesCacheUpdate = useCallback((updater) => {
    if (!codesCacheKey) return
    setCodesByService((prev) => {
      const nextEntry = typeof updater === 'function' ? updater(prev[codesCacheKey]) : updater
      return { ...prev, [codesCacheKey]: nextEntry }
    })
  }, [codesCacheKey])

  const openCodesForCategory = useCallback((categoryName) => {
    const name = (categoryName || '').trim()
    if (!name) return
    setCodesCategory(name)
    setCodesVisible(true)
  }, [])

  useEffect(() => {
    setCodesVisible(false)
    setCodesCategory(null)
  }, [selectedService, asOf])

  useEffect(() => {
    if (planTab === 'ask') setAskMounted(true)
  }, [planTab])

  /**
   * Hand a service off to Ask HRP. Passing a concrete CPT/HCPCS is what turns
   * HRP's "possible match" into an exact one, so the code-level entry point is
   * the more useful of the two.
   */
  const restoreBrowse = useCallback((snapshot) => {
    const from = snapshot || browseReturn
    if (from?.slot) setSelectedService(from.slot)
    if (from?.category) {
      setFocusCategory(from.category)
      setCodesCategory(from.category)
      setCodesVisible(true)
    } else if (from?.codesOpen) {
      setCodesVisible(true)
    }
  }, [browseReturn, setSelectedService])

  const goToAskFromBrowse = useCallback((categoryName, codeOrRow) => {
    const row = codeOrRow && typeof codeOrRow === 'object' ? codeOrRow : null
    const code = row?.code || (typeof codeOrRow === 'string' ? codeOrRow : '')
    const snapshot = {
      slot: selectedService,
      category: categoryName || focusCategory || null,
      codesOpen: true,
    }
    const defaults = askDefaultsForBrowse({
      slot: selectedService,
      category: snapshot.category,
      codeType: row?.codeTypeLabel || row?.codeType,
    })
    setBrowseReturn(snapshot)
    setAskPrefill?.({
      serviceCategory: snapshot.category,
      serviceCode: code || '',
      claimType: defaults.claimType,
      placeOfService: defaults.placeOfService,
    })
    askReturnTab.current = 'services'
    if (!window.history.state?.coverageAsk) {
      window.history.pushState({ ...(window.history.state || {}), coverageAsk: true }, '')
      askHistoryPushed.current = true
    }
    setPlanTab('ask')
  }, [selectedService, focusCategory, setAskPrefill, setPlanTab])

  const leaveAsk = useCallback((nextTab = 'services') => {
    if (nextTab === 'services') restoreBrowse()
    if (askHistoryPushed.current) {
      pendingTabAfterPop.current = nextTab
      askHistoryPushed.current = false
      window.history.back()
      return
    }
    setPlanTab(nextTab)
  }, [restoreBrowse, setPlanTab])

  const selectPlanTab = useCallback((id) => {
    if (id === planTab) return
    if (planTab === 'ask' && askHistoryPushed.current) {
      leaveAsk(id)
      return
    }
    if (id === 'ask' && planTab !== 'ask' && !window.history.state?.coverageAsk) {
      askReturnTab.current = planTab
      window.history.pushState({ ...(window.history.state || {}), coverageAsk: true }, '')
      askHistoryPushed.current = true
    }
    setPlanTab(id)
  }, [planTab, leaveAsk, setPlanTab])

  useEffect(() => {
    const onPop = () => {
      if (!pendingTabAfterPop.current && planTab !== 'ask') return
      const next = pendingTabAfterPop.current || askReturnTab.current || 'services'
      pendingTabAfterPop.current = null
      askHistoryPushed.current = false
      if (next === 'services') restoreBrowse()
      setPlanTab(next)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [planTab, restoreBrowse, setPlanTab])

  const checkServiceInAsk = goToAskFromBrowse

  const serviceNames = useMemo(() => {
    const fromApi = (coverage.serviceCategories || []).map((s) => s.name).filter(Boolean)
    const fromText = extractServiceCategoryNames(allProvisions)
    return [...new Set([...fromApi, ...fromText])].sort((a, b) => a.localeCompare(b))
  }, [coverage.serviceCategories, allProvisions])

  const slotGroups = useMemo(
    () => groupComponentsBySlot(coverage.components, extractServiceCategoryNames),
    [coverage.components],
  )

  const slotQuery = serviceQuery.trim().toLowerCase()
  const visibleSlotGroups = useMemo(() => {
    if (!slotQuery) return slotGroups
    return slotGroups.filter((group) => (
      group.slot.toLowerCase().includes(slotQuery)
      || group.components.some((c) => (c.name || '').toLowerCase().includes(slotQuery))
      || group.categories.some((name) => name.toLowerCase().includes(slotQuery))
    ))
  }, [slotGroups, slotQuery])

  const categoryHits = useMemo(() => {
    if (!slotQuery || slotQuery.length < 2) return []
    return serviceNames
      .filter((name) => name.toLowerCase().includes(slotQuery))
      .slice(0, 8)
      .map((name) => ({
        name,
        slots: slotsMentioningCategory(slotGroups, name),
      }))
  }, [serviceNames, slotQuery, slotGroups])

  const slotFamilies = useMemo(() => {
    const byFamily = new Map()
    for (const group of visibleSlotGroups) {
      const list = byFamily.get(group.family) || []
      list.push(group)
      byFamily.set(group.family, list)
    }
    return [...byFamily.entries()]
  }, [visibleSlotGroups])

  useEffect(() => {
    if (slotQuery) {
      setExpandedServiceGroups(new Set(slotFamilies.map(([family]) => family)))
      return
    }
    if (!selectedService) return
    const owner = slotGroups.find((g) => g.slot === selectedService)
    if (!owner) return
    setExpandedServiceGroups((prev) => {
      if (prev.has(owner.family)) return prev
      const next = new Set(prev)
      next.add(owner.family)
      return next
    })
  }, [selectedService, slotQuery, slotGroups, slotFamilies])

  const toggleServiceGroup = useCallback((group) => {
    setExpandedServiceGroups((prev) => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      return next
    })
  }, [])

  const selectSlot = useCallback((slot, category = null) => {
    setSelectedService(slot)
    setFocusCategory(category)
  }, [setSelectedService])

  const selectedSlotGroup = useMemo(
    () => slotGroups.find((g) => g.slot === selectedService) || null,
    [slotGroups, selectedService],
  )

  const serviceProvisions = useMemo(() => {
    if (!selectedSlotGroup) return []
    const fromSlot = (selectedSlotGroup.components || []).flatMap((c) =>
      (c.provisions || []).map((p) => ({
        ...p,
        slotName: c.slotName || selectedSlotGroup.slot,
        weight: c.weight ?? p.weight,
        componentName: c.name,
      }))
    )
    const focus = (focusCategory || '').trim().toLowerCase()
    const scoped = focus
      ? fromSlot.filter((p) => {
        const cats = extractServiceCategoryNames([p])
        return cats.some((c) => c.toLowerCase() === focus || c.toLowerCase().includes(focus))
          || (p.text || '').toLowerCase().includes(focus)
      })
      : fromSlot
    return collapseEquivalentProvisions(scoped, variableValues)
  }, [selectedSlotGroup, focusCategory, variableValues])

  // Untagged network (null/undefined) is not OON — same convention as the overview tab.
  const innProvisions = useMemo(
    () => serviceProvisions.filter((p) => p.isInNetwork !== false),
    [serviceProvisions]
  )
  const oonProvisions = useMemo(
    () => serviceProvisions.filter((p) => p.isInNetwork === false),
    [serviceProvisions]
  )

  const versionCount = coverage.plan.versions?.length || 0

  return (
    <div className="plan-view">
      <div className="coverage__plan-head">
        <div className="identity">
          <h1>{coverage.plan.name}</h1>
          <div className="ids">
            <code>{coverage.plan.hccId}</code>
            <StatusBadge status={coverage.plan.status} label={coverage.plan.statusLabel} />
            {coverage.plan.template && <span>{coverage.plan.template.name}</span>}
          </div>
        </div>
        {versionCount > 0 && (
          <button
            type="button"
            className="coverage__link-btn"
            onClick={() => setVersionsOpen((open) => !open)}
          >
            {versionsOpen ? "Hide versions" : `Show versions (${versionCount})`}
          </button>
        )}
      </div>

      {versionsOpen && versionCount > 0 && (
        <section className="section version-section">
          <p className="muted version-hint">Click a version to set As of to its start date.</p>
          <VersionChips versions={coverage.plan.versions} onSelect={onSelectVersion} />
        </section>
      )}

      <nav className="coverage__tabs" role="tablist" aria-label="Plan sections">
        {[
          ['overview', 'Overview'],
          ['services', 'Slots'],
          ['components', `Components (${coverage.components?.length || 0})`],
          ['riders', `Riders (${coverage.riders?.chosen?.length || 0})`],
          ['ask', 'Ask HRP'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={planTab === id}
            className={`coverage__tab ${planTab === id ? 'on' : ''}`}
            onClick={() => selectPlanTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {askMounted && (
        <div className="plan-panel answer-host scrollable-form" hidden={planTab !== 'ask'}>
          <CoverageAnswer
            plan={coverage.plan}
            asOf={asOf}
            prefill={askPrefill}
            onPrefillConsumed={() => setAskPrefill?.(null)}
            onBack={browseReturn ? () => leaveAsk('services') : undefined}
            backLabel={browseReturn?.slot
              ? `Back to ${browseReturn.slot}`
              : 'Back to slots'}
          />
        </div>
      )}

      {(planTab === 'services' || browseReturn) && (
        <div
          hidden={planTab !== 'services'}
          className={[
          'service-layout',
          !serviceNavOpen ? 'nav-collapsed' : '',
          codesVisible && activeCodesCategory ? 'codes-open' : '',
        ].filter(Boolean).join(' ')}>
          <aside className={`service-nav ${serviceNavOpen ? '' : 'collapsed'}`}>
            <div className="service-nav-top">
              <button
                type="button"
                className="service-nav-toggle"
                onClick={() => setServiceNavOpen((open) => !open)}
                title={serviceNavOpen ? 'Collapse services' : 'Expand services'}
                aria-label={serviceNavOpen ? 'Collapse services' : 'Expand services'}
                aria-expanded={serviceNavOpen}
              >
                {serviceNavOpen
                  ? <TbLayoutSidebarLeftCollapse size="1.15rem" color="var(--skyDark)" />
                  : <TbLayoutSidebarRightCollapse size="1.15rem" color="var(--darkGrey)" />}
              </button>
              {serviceNavOpen && <span className="service-nav-title">Slots</span>}
            </div>

            {serviceNavOpen ? (
              <>
                <input
                  className="filter-input"
                  value={serviceQuery}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  placeholder="Filter slots or a service category…"
                />
                {!!categoryHits.length && (
                  <div className="slot-category-hits">
                    <p className="service-group-label" style={{ cursor: 'default' }}>Service categories</p>
                    {categoryHits.map(({ name, slots }) => (
                      <button
                        key={name}
                        type="button"
                        className="service-item"
                        onClick={() => selectSlot(slots[0]?.slot || selectedService, name)}
                      >
                        {name}
                        {slots[0] && (
                          <span className="muted"> → {slots.map((s) => s.slot).join(', ')}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {!visibleSlotGroups.length && <p className="muted">No slots found.</p>}
                {slotFamilies.map(([family, items]) => {
                  const open = expandedServiceGroups.has(family)
                  return (
                    <div key={family} className={`service-group ${open ? 'open' : ''}`}>
                      <button
                        type="button"
                        className="service-group-label"
                        onClick={() => toggleServiceGroup(family)}
                        aria-expanded={open}
                      >
                        <span>{familyLabel(family)}</span>
                        <span className="service-group-meta">
                          <span className="service-group-count">{items.length}</span>
                          {open
                            ? <IoChevronUp size="0.8rem" />
                            : <IoChevronDown size="0.8rem" />}
                        </span>
                      </button>
                      {open && items.map((group) => (
                        <button
                          key={group.slot}
                          type="button"
                          className={`service-item ${selectedService === group.slot ? 'on' : ''}`}
                          onClick={() => selectSlot(group.slot)}
                        >
                          {group.slot}
                          {group.weight != null && (
                            <span className="muted"> · {group.weight}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )
                })}
              </>
            ) : (
              selectedService && (
                <button
                  type="button"
                  className="service-nav-selected"
                  title={selectedService}
                  onClick={() => setServiceNavOpen(true)}
                >
                  <span>{selectedService.slice(0, 1)}</span>
                </button>
              )
            )}
          </aside>

          <div className="service-detail">
            {!selectedService ? (
              <div className="empty-detail">
                <p>{serviceNavOpen ? 'Select a slot on the left' : 'Expand slots to browse the plan'}</p>
                <p className="muted">
                  Slots are how this plan is built. Search a service category to see which slot mentions it, or use Ask HRP for what the member pays.
                </p>
                {!serviceNavOpen && (
                  <button
                    type="button"
                    className="coverage__link-btn"
                    onClick={() => setServiceNavOpen(true)}
                  >
                    Show slots
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="service-detail-head">
                  <h2>{selectedService}</h2>
                  <div className="ids service-detail-actions">
                    {selectedSlotGroup?.weight != null && (
                      <span className="badge">Weight {selectedSlotGroup.weight}</span>
                    )}
                    <span>{serviceProvisions.length} provisions</span>
                    <span className="badge inn">INN · {innProvisions.length}</span>
                    <span className="badge oon">OON · {oonProvisions.length}</span>
                    <button
                      type="button"
                      className="coverage__link-btn"
                      onClick={() => checkServiceInAsk(categoryForSlotCodes(selectedSlotGroup, focusCategory) || selectedService)}
                      title="Ask HRP to evaluate a service in this slot"
                    >
                      Ask HRP
                    </button>
                  </div>
                </div>
                {!!selectedSlotGroup?.components?.length && (
                  <p className="muted">
                    {selectedSlotGroup.components.map((c) => c.name).filter(Boolean).join(' · ')}
                  </p>
                )}
                {!!selectedSlotGroup?.categories?.length && (
                  <div className="chip-wrap" style={{ padding: '0.4rem 0 0.75rem' }}>
                    {selectedSlotGroup.categories.slice(0, 24).map((name) => (
                      <button
                        key={name}
                        type="button"
                        className={`chip chip-btn ${focusCategory === name ? 'on' : ''}`}
                        onClick={() => setFocusCategory((prev) => (prev === name ? null : name))}
                      >
                        {name}
                      </button>
                    ))}
                    {selectedSlotGroup.categories.length > 24 && (
                      <span className="chip static">+{selectedSlotGroup.categories.length - 24} more</span>
                    )}
                  </div>
                )}
                {focusCategory && (
                  <p className="muted">
                    Showing rules that mention <strong>{focusCategory}</strong>
                    {' · '}
                    <button type="button" className="coverage__link-btn" onClick={() => setFocusCategory(null)}>
                      Clear
                    </button>
                  </p>
                )}

                <div className="service-subtabs">
                  <span className="service-subtab-label">Coverage</span>
                  <div className="service-subtab-btns" role="tablist" aria-label="Coverage view">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={coverageView === 'summary'}
                      className={coverageView === 'summary' ? 'on' : ''}
                      onClick={() => setCoverageView('summary')}
                    >
                      Summary
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={coverageView === 'provisions'}
                      className={coverageView === 'provisions' ? 'on' : ''}
                      onClick={() => setCoverageView('provisions')}
                    >
                      Provisions
                    </button>
                  </div>
                  <button
                    type="button"
                    className="coverage__link-btn service-codes-toggle"
                    onClick={() => {
                      if (codesVisible) {
                        setCodesVisible(false)
                        return
                      }
                      openCodesForCategory(categoryForSlotCodes(selectedSlotGroup, focusCategory))
                    }}
                  >
                    {codesVisible
                      ? 'Hide codes'
                      : codesCacheEntry
                        ? `Show codes (${codesCacheEntry.total ?? codesCacheEntry.codes?.length ?? 0})`
                        : 'Show codes'}
                  </button>
                </div>
                <div className="inn-oon-grid">
                  <div className="inn-oon-col inn">
                    <div className="inn-oon-head">
                      <span className="badge inn">In network</span>
                      <span className="badge count inn">{innProvisions.length}</span>
                    </div>
                    {coverageView === 'summary' ? (
                      <ProvisionSummaryList
                        provisions={innProvisions}
                        variableValues={variableValues}
                        focusService={focusCategory}
                        onOpenCodes={openCodesForCategory}
                      />
                    ) : (
                      <ProvisionList
                        provisions={innProvisions}
                        variableValues={variableValues}
                        focusService={focusCategory}
                        onOpenCodes={openCodesForCategory}
                      />
                    )}
                  </div>
                  <div className="inn-oon-col oon">
                    <div className="inn-oon-head">
                      <span className="badge oon">Out of network</span>
                      <span className="badge count oon">{oonProvisions.length}</span>
                    </div>
                    {coverageView === 'summary' ? (
                      <ProvisionSummaryList
                        provisions={oonProvisions}
                        variableValues={variableValues}
                        focusService={focusCategory}
                        onOpenCodes={openCodesForCategory}
                      />
                    ) : (
                      <ProvisionList
                        provisions={oonProvisions}
                        variableValues={variableValues}
                        focusService={focusCategory}
                        onOpenCodes={openCodesForCategory}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {codesVisible && activeCodesCategory && (
            <CategoryCodesSidebar
              name={activeCodesCategory}
              asOf={asOf}
              cacheEntry={codesCacheEntry}
              onCacheUpdate={onCodesCacheUpdate}
              onHide={() => setCodesVisible(false)}
              fromProvision={!!codesCategory && codesCategory !== focusCategory}
              onCheckCode={(code) => checkServiceInAsk(activeCodesCategory, code)}
            />
          )}
        </div>
      )}

      {planTab === 'overview' && (
        <div className="plan-panel overview-panel">
          <section className="overview-block">
            <header className="overview-block-head overview-block-head--row">
              <div>
                <h2>Premium &amp; coverage tiers</h2>
                <p className="muted">Enrollment tiers and account billing rates</p>
              </div>
              <button
                type="button"
                className="coverage__link-btn"
                onClick={() => setPremiumsOpen((open) => !open)}
                aria-expanded={premiumsOpen}
              >
                {premiumsOpen ? 'Hide' : 'Show'}
              </button>
            </header>
            {premiumsOpen && (
              <PremiumTierOverview plan={coverage.plan} premiums={coverage.premiums} />
            )}
          </section>

          <section className="overview-block">
            <header className="overview-block-head">
              <h2>Deductibles &amp; out-of-pocket</h2>
              <p className="muted">Plan-year limits at a glance</p>
            </header>
            <PlanCostShareOverview
              provisions={[
                ...(allProvisions || []).filter((p) => /Deductible|OutOfPocket/i.test(p.type || '')),
                ...((accumulatorSetup?.riders || []).flatMap((r) => r.dedOopProvisions || [])),
              ]}
              variableValues={variableValues}
              setup={accumulatorSetup?.setup}
            />
          </section>

          <nav className="tabs overview-tabs" aria-label="Overview sections">
            {[
              ['accumulators', 'Accumulators', accumulatorSetup?.riders?.length],
              ['networks', 'Networks', accumulatorSetup?.networkRiders?.length
                ?? coverage.networks?.length],
              ['repricers', 'Repricers', accumulatorSetup?.repricers?.length],
              ['search', 'Search', null],
              ['services', 'Slots', slotGroups.length],
            ].map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                className={overviewTab === id ? 'on' : ''}
                onClick={() => setOverviewTab(id)}
              >
                {label}
                {count != null ? ` (${count})` : ''}
              </button>
            ))}
          </nav>

          {overviewTab === 'accumulators' && (
            <section className="overview-block">
              <header className="overview-block-head">
                <h2>Accumulator setup</h2>
                <p className="muted">How deductibles and OOP apply across tiers</p>
              </header>
              {!accumulatorSetup ? (
                <div className="accum-setup" aria-busy="true" aria-label="Loading accumulator setup">
                  <Skeleton width="70%" height="0.9rem" className="mb-sm" />
                  <Skeleton width="55%" height="0.75rem" />
                </div>
              ) : (
                <AccumSetupCard
                  setup={accumulatorSetup.setup}
                  riders={accumulatorSetup.riders}
                  variableValues={variableValues}
                  allProvisions={allProvisions}
                />
              )}
            </section>
          )}

          {overviewTab === 'networks' && (
            <section className="overview-block">
              <header className="overview-block-head">
                <h2>Networks</h2>
                <p className="muted">How this plan decides who is in-network</p>
              </header>
              {!accumulatorSetup ? (
                <div aria-busy="true" aria-label="Loading networks">
                  <Skeleton width="70%" height="0.9rem" className="mb-sm" />
                  <Skeleton width="55%" height="2.5rem" />
                </div>
              ) : (
                <NetworkSection
                  networkRiders={accumulatorSetup.networkRiders}
                  benefitNetworks={
                    (coverage.networks || []).filter((n) => n.source !== 'networkDefinition')
                  }
                  openRiderModal={openRiderModal}
                  asOf={asOf}
                />
              )}
            </section>
          )}

          {overviewTab === 'repricers' && (
            <section className="overview-block">
              <header className="overview-block-head">
                <h2>Repricers</h2>
                <p className="muted">External pricing vendors used before benefits</p>
              </header>
              {!accumulatorSetup ? (
                <div aria-busy="true" aria-label="Loading repricers">
                  <Skeleton width="70%" height="0.9rem" className="mb-sm" />
                  <Skeleton width="55%" height="2.5rem" />
                </div>
              ) : (
                <RepricerSection
                  repricers={accumulatorSetup.repricers}
                  openRiderModal={openRiderModal}
                />
              )}
            </section>
          )}

          {overviewTab === 'search' && (
            <section className="overview-block">
              <header className="overview-block-head">
                <h2>Quick search</h2>
                <p className="muted">Search provisions across the whole plan</p>
              </header>
              <input
                className="filter-input overview-search"
                value={answerFilter}
                onChange={(e) => setAnswerFilter(e.target.value)}
                placeholder="Search all provisions…"
                autoFocus
              />
              {answerFilter.trim() ? (
                <ProvisionList provisions={allProvisions} filter={answerFilter} />
              ) : (
                <p className="muted overview-empty">Type to search, or use Slots for a focused view.</p>
              )}
            </section>
          )}

          {overviewTab === 'services' && (
            <section className="overview-block">
              <header className="overview-block-head">
                <h2>Slots on this plan</h2>
                <p className="muted">
                  {slotGroups.length} named slots — open one for the component and its rules
                </p>
              </header>
              <div className="chip-wrap overview-service-chips">
                {slotGroups.slice(0, 48).map((group) => (
                  <button
                    key={group.slot}
                    type="button"
                    className="chip"
                    onClick={() => {
                      selectSlot(group.slot)
                      setPlanTab('services')
                    }}
                  >
                    {group.slot}
                  </button>
                ))}
                {slotGroups.length > 48 && (
                  <button type="button" className="chip" onClick={() => setPlanTab('services')}>
                    +{slotGroups.length - 48} more
                  </button>
                )}
              </div>
              {!serviceNames.length && <p className="muted overview-empty">No service categories found.</p>}
            </section>
          )}
        </div>
      )}

      {planTab === 'components' && (
        <div className="plan-panel">
          <section className="section">
            <div className="section-head">
              <h2>Components</h2>
              <span className="muted">{coverage.components?.length || 0}</span>
            </div>
            <input
              className="filter-input"
              value={answerFilter}
              onChange={(e) => setAnswerFilter(e.target.value)}
              placeholder="Filter components…"
            />
            <div className="row-list">
              {(coverage.components || [])
                .filter((c) => {
                  const q = answerFilter.trim().toLowerCase()
                  if (!q) return true
                  return c.name.toLowerCase().includes(q)
                    || (c.provisions || []).some((p) => (p.text || '').toLowerCase().includes(q))
                })
                .map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className="row"
                    onClick={() => setProvisionModal({
                      title: c.name,
                      subtitle: `${c.provisionCount ?? c.provisions?.length ?? 0} provisions`,
                      provisions: c.provisions || [],
                    })}
                  >
                    <div>
                      <div className="name">{c.name}</div>
                      <div className="sub">
                        {[c.slotName || inferSlotName(c), c.weight != null ? `weight ${c.weight}` : null].filter(Boolean).join(' · ') || c.description}
                      </div>
                    </div>
                    <span className="muted">{c.provisionCount ?? c.provisions?.length ?? 0}</span>
                  </button>
                ))}
            </div>
          </section>
        </div>
      )}

      {planTab === 'riders' && (
        <div className="plan-panel">
          <section className="section">
            <div className="section-head">
              <h2>Riders</h2>
              <span className="muted">
                {coverage.riders?.chosen?.length || 0} chosen · {coverage.riders?.availableCount || 0} available
              </span>
            </div>
            <div className="tabs">
              <button type="button" className={riderTab === 'chosen' ? 'on' : ''} onClick={() => setRiderTab('chosen')}>Chosen</button>
              <button type="button" className={riderTab === 'available' ? 'on' : ''} onClick={() => setRiderTab('available')}>Available</button>
            </div>

            {riderTab === 'chosen' && (
              <div className="row-list">
                {(coverage.riders?.chosen || []).map((r) => (
                  <button key={r.key} type="button" className="row" onClick={() => openRiderModal(r.key)}>
                    <div>
                      <div className="name">{r.name}</div>
                      <div className="sub mono">{r.hccId}</div>
                    </div>
                    <span className="muted">{r.componentCount ?? 0}</span>
                  </button>
                ))}
                {!coverage.riders?.chosen?.length && <p className="muted">No chosen riders.</p>}
              </div>
            )}

            {riderTab === 'available' && (
              <div>
                <input
                  className="filter-input"
                  value={riderQ}
                  onChange={(e) => setRiderQ(e.target.value)}
                  placeholder="Filter available riders"
                  onKeyDown={(e) => { if (e.key === 'Enter') loadAvailableRiders(riderQ, 0) }}
                />
                <p className="muted" style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                  Not on this plan — template only.
                </p>
                {ridersLoading && !availableRiders ? (
                  <ListRowsSkeleton rows={6} />
                ) : (
                  <div className="row-list">
                    {(availableRiders?.riders || []).map((r) => (
                      <button key={r.key} type="button" className="row" onClick={() => openRiderModal(r.key)}>
                        <div>
                          <div className="name">{r.name}</div>
                          <div className="sub mono">{r.hccId}</div>
                        </div>
                        <span className="badge available">Available</span>
                      </button>
                    ))}
                  </div>
                )}
                {availableRiders && (
                  <div className="pager">
                    <button type="button" className="btn secondary" disabled={availableRiders.offset <= 0} onClick={() => loadAvailableRiders(riderQ, Math.max(0, availableRiders.offset - availableRiders.limit))}>Prev</button>
                    <span>{availableRiders.offset + 1}–{Math.min(availableRiders.offset + availableRiders.limit, availableRiders.total)} of {availableRiders.total}</span>
                    <button type="button" className="btn secondary" disabled={availableRiders.offset + availableRiders.limit >= availableRiders.total} onClick={() => loadAvailableRiders(riderQ, availableRiders.offset + availableRiders.limit)}>Next</button>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {provisionModal && (
        <ProvisionModal
          title={provisionModal.title}
          subtitle={provisionModal.subtitle}
          provisions={provisionModal.provisions}
          components={provisionModal.components}
          loading={provisionModal.loading}
          variableValues={variableValues}
          onClose={() => setProvisionModal(null)}
        />
      )}
    </div>
  )
}

export { PlanView, VersionChips, StatusBadge };
